import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle2, XCircle, Clock, Plus, Trash2, Radio, Megaphone, CalendarDays, Users, ChevronDown, Crown, Lock, Edit2, X, MapPin, Package, Tag, Settings, ToggleLeft, ToggleRight, Mail, Server, Globe, Save, MessageSquare, UserCheck, UserX, AlertTriangle, Zap, RefreshCw, Tv2 } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import { qsoAPI, annonceAPI, evenementAPI, adminAPI, stammAPI, settingsAPI } from '../services/api';
import Toast from '../components/Toast';

const useWindowWidth = () => {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 800);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
};

const API_BASE = process.env.REACT_APP_API_URL?.replace('/v2/api', '') || 'http://localhost:3001';

const S = {
  card: { backgroundColor:'white', border:'1px solid #E5E7EB', borderRadius:10, boxShadow:'0 1px 3px rgba(0,0,0,0.06)' },
  badge: (c) => ({ display:'inline-flex', alignItems:'center', gap:4, fontSize:'0.6875rem', fontWeight:700, padding:'3px 9px', borderRadius:20, ...c }),
  btnPrimary: { display:'inline-flex', alignItems:'center', gap:5, backgroundColor:'#111827', color:'white', border:'none', borderRadius:7, padding:'5px 10px', fontSize:'0.75rem', fontWeight:600, cursor:'pointer' },
  btnDanger: { display:'inline-flex', alignItems:'center', gap:5, backgroundColor:'#FEF2F2', color:'#DC2626', border:'1px solid #FECACA', borderRadius:7, padding:'5px 10px', fontSize:'0.75rem', fontWeight:600, cursor:'pointer' },
  btnSuccess: { display:'inline-flex', alignItems:'center', gap:5, backgroundColor:'#F0FDF4', color:'#15803D', border:'1px solid #BBF7D0', borderRadius:7, padding:'5px 10px', fontSize:'0.75rem', fontWeight:600, cursor:'pointer' },
  btnGhost: { display:'inline-flex', alignItems:'center', gap:5, backgroundColor:'transparent', color:'#6B7280', border:'1px solid #E5E7EB', borderRadius:7, padding:'5px 10px', fontSize:'0.75rem', fontWeight:500, cursor:'pointer' },
  inp: { width:'100%', padding:'8px 10px', border:'1px solid #D1D5DB', borderRadius:6, fontSize:'0.875rem', outline:'none', boxSizing:'border-box', color:'#111827' },
  label: { display:'block', fontSize:'0.75rem', fontWeight:600, color:'#374151', marginBottom:4 },
};

const ROLE_STYLES = {
  membre:  { bg:'#F3F4F6', c:'#374151', border:'#E5E7EB', avatarBg:'#E5E7EB', avatarC:'#4B5563', label:'Membre' },
  comite:  { bg:'#EFF6FF', c:'#1D4ED8', border:'#BFDBFE', avatarBg:'#DBEAFE', avatarC:'#1D4ED8', label:'Comité' },
  admin:   { bg:'#F5F3FF', c:'#6D28D9', border:'#DDD6FE', avatarBg:'#EDE9FE', avatarC:'#6D28D9', label:'Admin' },
};

const ComitePage = () => {
  const { isComite, isAdmin } = useAuth();
  const navigate = useNavigate();
  const isMobile = useWindowWidth() < 600;

  // Données
  const [pendingQSO, setPendingQSO] = useState([]);
  const [allQSO, setAllQSO] = useState([]);
  const [pendingAnnonces, setPendingAnnonces] = useState([]);
  const [allAnnonces, setAllAnnonces] = useState([]);
  const [pendingEvenements, setPendingEvenements] = useState([]);
  const [allEvenements, setAllEvenements] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState('qso');
  const [expandedDesc, setExpandedDesc] = useState({});

  // Formulaires
  const [qsoForm, setQsoForm] = useState({ date:'', heure:'11:00' });
  const [showQsoForm, setShowQsoForm] = useState(false);
  const [annonceForm, setAnnonceForm] = useState({ titre:'', contenu:'', type:'Vente' });
  const [showAnnonceForm, setShowAnnonceForm] = useState(false);
  const [evenementForm, setEvenementForm] = useState({ titre:'', description:'', date_debut:'', lieu:'', lien:'' });
  const [showEvenementForm, setShowEvenementForm] = useState(false);
  const [editEvenement, setEditEvenement] = useState(null);

  // Modal de confirmation
  const [confirm, setConfirm] = useState(null); // { message, onConfirm }
  const [selectedAnnonce, setSelectedAnnonce] = useState(null);

  // Modal de refus
  const [rejectModal, setRejectModal] = useState(null); // { type, id, title }
  const [rejectReason, setRejectReason] = useState('');

  // Settings
  const [settings, setSettings] = useState({});
  const [settingsForm, setSettingsForm] = useState({});
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null); // { ok: bool, msg: string }
  const [testSignalSending, setTestSignalSending] = useState(false);
  const [testSignalResult, setTestSignalResult] = useState(null); // { ok: bool, msg: string }
  const [signalWizard, setSignalWizard] = useState(false); // afficher le wizard
  const [wizardStep, setWizardStep] = useState(1); // 1=register 2=verify 3=groups
  const [wizardNumber, setWizardNumber] = useState('');
  const [wizardCode, setWizardCode] = useState('');
  const [wizardLoading, setWizardLoading] = useState(false);
  const [wizardMsg, setWizardMsg] = useState(null); // { ok, text }
  const [wizardGroups, setWizardGroups] = useState([]);
  const [wizardCaptcha, setWizardCaptcha] = useState('');
  const [wizardNeedCaptcha, setWizardNeedCaptcha] = useState(false);
  const [wizardNewGroupName, setWizardNewGroupName] = useState('');
  const [wizardMode, setWizardMode] = useState(null); // null | 'link' | 'register'
  const [wizardQR, setWizardQR] = useState(null); // { qr, uri }
  const [wizardLinkNumber, setWizardLinkNumber] = useState('');
  const [signalGroupLink, setSignalGroupLink] = useState(null); // { link, groupId, loading, error }

  // Stamm
  const [stammSessions, setStammSessions] = useState([]);
  const [stammInscriptions, setStammInscriptions] = useState({}); // { sessionId: [] }
  const [annulModal, setAnnulModal] = useState(null); // { session }
  const [annulRaison, setAnnulRaison] = useState('');
  const [showStammForm, setShowStammForm] = useState(false);
  const [stammForm, setStammForm] = useState({ date:'', heure:'20:00', description:'' });
  const [pendingUsers, setPendingUsers] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, name }
  const [openRoleDropdown, setOpenRoleDropdown] = useState(null); // userId
  const [generateResult, setGenerateResult] = useState(null); // { created, skipped, message }
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!isComite) { navigate('/qso'); return; }
    loadAll();
  }, [isComite, navigate]); // eslint-disable-line

  const loadAll = async () => {
    setLoading(true);
    try {
      const calls = {
        qso: qsoAPI.getAll(),
        annonces: annonceAPI.getPending(),
        annoncesAll: annonceAPI.getAll(),
        evPend: evenementAPI.getPending(),
        evAll: evenementAPI.getAll(),
        qsoAll: adminAPI.getQSOAll(),
        stamm: stammAPI.getAllAdmin(),
      };
      if (isComite) calls.users = adminAPI.getUsers();
      if (isComite) calls.pendingUsers = adminAPI.getPendingUsers();
      if (isAdmin) calls.settings = settingsAPI.get();

      const keys = Object.keys(calls);
      const results = await Promise.all(Object.values(calls));
      const r = {};
      keys.forEach((k, i) => { r[k] = results[i]; });

      setPendingQSO(r.qso.data.filter(q => q.statut === 'pending'));
      setPendingAnnonces(r.annonces.data);
      if (r.annoncesAll) setAllAnnonces(r.annoncesAll.data);
      setPendingEvenements(r.evPend.data);
      setAllEvenements(r.evAll.data);
      setAllQSO(r.qsoAll.data);
      setStammSessions(r.stamm.data);
      if (r.users) setUsers(r.users.data);
      if (r.pendingUsers) setPendingUsers(r.pendingUsers.data);
      if (r.settings) { setSettings(r.settings.data); setSettingsForm(r.settings.data); }
    } catch (e) { showToast('Erreur de chargement', 'error'); }
    finally { setLoading(false); }
  };

  const showToast = (msg, type = 'success') => setToast({ message: msg, type });

  const handleApproveUser = async (userId) => {
    try {
      await adminAPI.approveUser(userId);
      showToast('Compte approuvé.');
      loadAll();
    } catch (e) { showToast(e.response?.data?.error || 'Erreur', 'error'); }
  };

  const handleDisableUser = async (userId, disabled) => {
    try {
      await adminAPI.disableUser(userId, disabled);
      showToast(disabled ? 'Compte désactivé.' : 'Compte réactivé.');
      loadAll();
    } catch (e) { showToast(e.response?.data?.error || 'Erreur', 'error'); }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await adminAPI.deleteUser(userId);
      showToast('Compte et données supprimés.');
      setConfirmDelete(null);
      loadAll();
    } catch (e) { showToast(e.response?.data?.error || 'Erreur', 'error'); }
  };

  const handleValidateQSO = async (id, approved, reason) => {
    if (!approved && reason === undefined) { setRejectModal({ type:'qso', id, title:'cette proposition QSO' }); setRejectReason(''); return; }
    try { await qsoAPI.validate(id, approved, reason || null); showToast(approved ? 'QSO validé.' : 'Refusé.'); loadAll(); }
    catch (e) { showToast(e.response?.data?.error || 'Erreur', 'error'); }
  };

  const handleValidateAnnonce = async (id, approved, reason) => {
    if (!approved && reason === undefined) { setRejectModal({ type:'annonce', id, title:'cette annonce' }); setRejectReason(''); return; }
    try { await annonceAPI.validate(id, approved, reason || null); showToast(approved ? 'Annonce publiée.' : 'Refusée.'); loadAll(); }
    catch (e) { showToast(e.response?.data?.error || 'Erreur', 'error'); }
  };

  const handleValidateEvenement = async (id, approved, reason) => {
    if (!approved && reason === undefined) { setRejectModal({ type:'evenement', id, title:'cet événement' }); setRejectReason(''); return; }
    try { await evenementAPI.validate(id, approved, reason || null); showToast(approved ? 'Événement publié.' : 'Refusé.'); loadAll(); }
    catch (e) { showToast(e.response?.data?.error || 'Erreur', 'error'); }
  };

  const confirmReject = () => {
    if (!rejectModal) return;
    const { type, id } = rejectModal;
    setRejectModal(null);
    if (type === 'qso') handleValidateQSO(id, false, rejectReason);
    else if (type === 'annonce') handleValidateAnnonce(id, false, rejectReason);
    else if (type === 'evenement') handleValidateEvenement(id, false, rejectReason);
  };

  const handleCreateQSO = async (e) => {
    e.preventDefault();
    try { await adminAPI.createQSOSession(qsoForm); showToast('Session créée.'); setShowQsoForm(false); setQsoForm({ date:'', heure:'11:00' }); loadAll(); }
    catch (e) { showToast(e.response?.data?.error || 'Erreur', 'error'); }
  };

  const openConfirm = (message, onConfirm) => setConfirm({ message, onConfirm });

  const handleDeleteQSO = (id) => openConfirm(
    'Supprimer définitivement cette session QSO ?',
    async () => { try { await adminAPI.deleteQSOSession(id); showToast('Session supprimée.'); loadAll(); } catch (e) { showToast(e.response?.data?.error || 'Erreur', 'error'); } }
  );

  const handleReleaseQSO = (id, capitaine) => openConfirm(
    `Libérer la session de ${capitaine} ? Le capitaine sera retiré et la session redeviendra disponible pour inscription.`,
    async () => { try { await adminAPI.releaseQSOSession(id); showToast('Session libérée.'); loadAll(); } catch (e) { showToast(e.response?.data?.error || 'Erreur', 'error'); } }
  );

  const handleCreateAnnonce = async (e) => {
    e.preventDefault();
    try { await annonceAPI.create(annonceForm); showToast('Annonce publiée.'); setShowAnnonceForm(false); setAnnonceForm({ titre:'', contenu:'', type:'Vente' }); loadAll(); }
    catch (e) { showToast(e.response?.data?.error || 'Erreur', 'error'); }
  };

  const handleSaveEvenement = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...evenementForm,
        date_debut: evenementForm.debut_date && evenementForm.debut_heure ? `${evenementForm.debut_date}T${evenementForm.debut_heure}` : evenementForm.debut_date,
        date_fin: evenementForm.fin_date && evenementForm.fin_heure ? `${evenementForm.fin_date}T${evenementForm.fin_heure}` : (evenementForm.fin_date || null),
      };
      if (editEvenement) {
        await evenementAPI.update(editEvenement.id, payload);
        showToast('Événement mis à jour.');
      } else {
        await evenementAPI.create(payload);
        showToast('Événement créé.');
      }
      setShowEvenementForm(false); setEditEvenement(null);
      setEvenementForm({ titre:'', description:'', debut_date:'', debut_heure:'', fin_date:'', fin_heure:'', lieu:'', lien:'', type:'QSO' });
      loadAll();
    } catch (e) { showToast(e.response?.data?.error || 'Erreur', 'error'); }
  };

  const handleDeleteEvenement = (id) => openConfirm(
    'Supprimer définitivement cet événement ?',
    async () => { try { await evenementAPI.delete(id); showToast('Événement supprimé.'); loadAll(); } catch (e) { showToast(e.response?.data?.error || 'Erreur', 'error'); } }
  );

  const handleValiderCandidature = async (sessionId, approved) => {
    try {
      await stammAPI.valider(sessionId, approved);
      showToast(approved ? 'Candidature confirmée.' : 'Candidature refusée.');
      loadAll();
    } catch (e) { showToast(e.response?.data?.error || 'Erreur', 'error'); }
  };

  const handleAnnulerSession = async () => {
    if (!annulModal) return;
    try {
      await stammAPI.annulerSession(annulModal.session.id, annulRaison);
      showToast('Session annulée.');
      setAnnulModal(null); setAnnulRaison('');
      loadAll();
    } catch (e) { showToast(e.response?.data?.error || 'Erreur', 'error'); }
  };

  const handleRouvrirSession = (id) => openConfirm(
    'Réouvrir cette session Stamm ?',
    async () => { try { await stammAPI.rouvrirSession(id); showToast('Session réouverte.'); loadAll(); } catch (e) { showToast('Erreur', 'error'); } }
  );

  const handleCreateStamm = async (e) => {
    e.preventDefault();
    try {
      await stammAPI.createSession(stammForm);
      showToast('Session Stamm créée.');
      setShowStammForm(false);
      setStammForm({ date:'', heure:'20:00', description:'' });
      loadAll();
    } catch (e) { showToast(e.response?.data?.error || 'Erreur', 'error'); }
  };

  const handleDeleteStamm = (id) => openConfirm(
    'Supprimer définitivement cette session Stamm ?',
    async () => { try { await stammAPI.deleteSession(id); showToast('Session supprimée.'); loadAll(); } catch (e) { showToast('Erreur', 'error'); } }
  );

  const handleUpdateRole = async (userId, role) => {
    try { await adminAPI.updateUserRole(userId, role); showToast('Rôle mis à jour.'); loadAll(); }
    catch (e) { showToast(e.response?.data?.error || 'Erreur', 'error'); }
  };

  const splitDatetime = (iso) => {
    if (!iso) return { date:'', heure:'' };
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60000);
    const str = local.toISOString();
    return { date: str.substring(0,10), heure: str.substring(11,16) };
  };

  const openEditEvenement = (ev) => {
    setEditEvenement(ev);
    const deb = splitDatetime(ev.date_debut);
    const fin = splitDatetime(ev.date_fin);
    setEvenementForm({
      titre: ev.titre, description: ev.description,
      debut_date: deb.date, debut_heure: deb.heure,
      fin_date: fin.date, fin_heure: fin.heure,
      lieu: ev.lieu || '', lien: ev.lien || '', type: ev.type || 'Autre'
    });
    setShowEvenementForm(true);
  };

  if (loading) return (
    <div style={{display:'flex',justifyContent:'center',alignItems:'center',minHeight:300}}>
      <div style={{width:28,height:28,border:'2px solid #111827',borderTopColor:'transparent',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/>
    </div>
  );

  const stammPending = stammSessions.filter(s => s.statut === 'pending').length;
  const totalPending = pendingQSO.length + pendingAnnonces.length + pendingEvenements.length + stammPending;

  const TABS = [
    { id:'qso', label:'QSO', icon:<Radio style={{width:13,height:13}}/>, count: pendingQSO.length },
    { id:'annonces', label:'Annonces', icon:<Megaphone style={{width:13,height:13}}/>, count: pendingAnnonces.length },
    { id:'agenda', label:'Agenda', icon:<CalendarDays style={{width:13,height:13}}/>, count: pendingEvenements.length },
    { id:'stamm', label:'Stamm', icon:<Users style={{width:13,height:13}}/>, count: stammPending || null },
    { id:'users', label:'Membres', icon:<Shield style={{width:13,height:13}}/>, count: null },
    { id:'settings', label:'Config', icon:<Settings style={{width:13,height:13}}/>, count: null },
  ];

  const isAdminRole = isAdmin;

  return (
    <div style={{display:'flex',flexDirection:'column',gap:20}}>

      {/* ── Header contextuel ── */}
      <div style={{
        borderRadius:10, padding:'14px 16px',
        background: isAdminRole
          ? 'linear-gradient(135deg, #1E1B4B 0%, #312E81 60%, #4C1D95 100%)'
          : 'linear-gradient(135deg, #1E3A5F 0%, #1D4ED8 60%, #2563EB 100%)',
        display:'flex', alignItems:'center', justifyContent:'space-between', gap:12,
        boxShadow: isAdminRole ? '0 4px 24px rgba(109,40,217,0.25)' : '0 4px 24px rgba(29,78,216,0.25)',
      }}>
        {/* Titre + statut */}
        <div style={{display:'flex',alignItems:'center',gap:10,flex:1,minWidth:0}}>
          <div style={{
            width:36,height:36,borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,
            backgroundColor: isAdminRole ? 'rgba(167,139,250,0.25)' : 'rgba(147,197,253,0.25)',
          }}>
            {isAdminRole
              ? <Crown style={{width:18,height:18,color:'#C4B5FD'}}/>
              : <Shield style={{width:18,height:18,color:'#93C5FD'}}/>}
          </div>
          <div style={{minWidth:0}}>
            <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
              <span style={{fontWeight:800,fontSize:'0.9375rem',color:'white',whiteSpace:'nowrap'}}>
                {isAdminRole ? 'Espace Admin' : 'Espace Comité'}
              </span>
              <span style={{fontSize:'0.6rem',fontWeight:700,padding:'1px 6px',borderRadius:20,backgroundColor:'rgba(255,255,255,0.15)',color:'rgba(255,255,255,0.8)'}}>
                {isAdminRole ? 'ADMIN' : 'COMITÉ'}
              </span>
            </div>
            <div style={{fontSize:'0.75rem',color:'rgba(255,255,255,0.55)',marginTop:1}}>
              {totalPending > 0
                ? <span style={{color:'#FCA5A5',fontWeight:600}}>⚠ {totalPending} en attente</span>
                : 'Tout est à jour ✓'}
            </div>
          </div>
        </div>

        {/* KPI compacts */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:4,flexShrink:0,minWidth: isMobile ? 0 : 'auto'}}>
          {[
            { label:'QSO', val: pendingQSO.length, accent: pendingQSO.length>0 },
            { label:'ANN.', val: pendingAnnonces.length, accent: pendingAnnonces.length>0 },
            { label:'EV.', val: pendingEvenements.length, accent: pendingEvenements.length>0 },
            { label:'MBR', val: users.filter(u=>u.role==='membre').length, accent: false },
          ].map(k => (
            <div key={k.label} style={{
              borderRadius:6, padding:'4px 6px', textAlign:'center', minWidth: isMobile ? 32 : 34,
              backgroundColor: k.accent ? 'rgba(252,165,165,0.15)' : 'rgba(255,255,255,0.08)',
              border: `1px solid ${k.accent ? 'rgba(252,165,165,0.3)' : 'rgba(255,255,255,0.1)'}`,
            }}>
              <div style={{fontSize: isMobile ? '0.875rem' : '0.9375rem',fontWeight:800,color: k.accent ? '#FCA5A5' : 'white',lineHeight:1}}>{k.val}</div>
              <div style={{fontSize:'0.5rem',fontWeight:600,color:'rgba(255,255,255,0.4)',marginTop:2,textTransform:'uppercase'}}>{k.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Onglets ── */}
      <div style={{display:'flex',gap:0,borderBottom:'2px solid #F3F4F6',overflowX:'auto',WebkitOverflowScrolling:'touch',scrollbarWidth:'none',msOverflowStyle:'none'}}>
        {TABS.filter(t => !(( t.id === 'users' || t.id === 'settings') && !isAdminRole)).map(t => {
          const isRestrictedTab = t.id === 'users' || t.id === 'settings';
          const isActive = activeTab === t.id;
          const accentColor = isRestrictedTab ? '#6D28D9' : '#111827';
          return (
            <button key={t.id} onClick={()=>setActiveTab(t.id)} style={{
              display:'flex',flexDirection:'column',alignItems:'center',gap:2,padding:'6px 8px',border:'none',background:'none',cursor:'pointer',
              fontSize:'0.625rem',fontWeight: isActive ? 700 : 500,whiteSpace:'nowrap',flexShrink:0,flex:1,
              color: isActive ? accentColor : '#9CA3AF',
              borderBottom: isActive ? `2px solid ${accentColor}` : '2px solid transparent',
              marginBottom:-2, position:'relative',
              opacity: isRestrictedTab && !isAdminRole ? 0.45 : 1,
            }}>
              <span style={{position:'relative',display:'inline-flex'}}>
                {t.icon}
                {t.count > 0 && <span style={{position:'absolute',top:-4,right:-6,backgroundColor:'#DC2626',color:'white',fontSize:'0.45rem',fontWeight:700,padding:'1px 3px',borderRadius:9999,minWidth:12,textAlign:'center',lineHeight:1.4}}>{t.count}</span>}
              </span>
              {t.label}
              {isRestrictedTab && !isAdminRole && <Lock style={{width:8,height:8,color:'#9CA3AF'}}/>}
            </button>
          );
        })}
      </div>

      {/* ══════════════════════════════════════════
          TAB QSO
      ══════════════════════════════════════════ */}
      {activeTab === 'qso' && (
        <div style={{display:'flex',flexDirection:'column',gap:16}}>

          {/* Sous-section : Validations en attente */}
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,flexWrap:'wrap'}}>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'#6B7280',textTransform:'uppercase',letterSpacing:'0.08em'}}>
              En attente ({pendingQSO.length})
            </div>
          </div>

          {pendingQSO.length === 0 ? (
            <div style={{...S.card,padding:'24px',textAlign:'center',color:'#9CA3AF',fontSize:'0.875rem'}}>Aucune proposition en attente.</div>
          ) : pendingQSO.map(qso => (
            <div key={qso.id} style={{...S.card,padding:'14px 16px',borderLeft:'3px solid #F59E0B'}}>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8,marginBottom:6}}>
                <div style={{fontWeight:700,fontSize:'0.9375rem',color:'#111827',textTransform:'capitalize',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                  {format(new Date(qso.date),'EEEE dd MMMM',{locale:fr})}
                </div>
                <span style={{...S.badge({backgroundColor:'#FEF9C3',color:'#713F12'}),flexShrink:0}}>
                  <Clock style={{width:10,height:10}}/> En attente
                </span>
              </div>
              <div style={{fontSize:'0.8125rem',color:'#6B7280',marginBottom:10}}>
                Capitaine : <strong style={{color:'#111827'}}>
                  {qso.capitaine_nom_libre || `${qso.capitaine_prenom||''} ${qso.capitaine_nom||''}`.trim()}
                </strong>
                {' · '}
                <span style={{fontFamily:'monospace',fontWeight:700}}>
                  {qso.capitaine_indicatif_libre || qso.capitaine_indicatif}
                </span>
              </div>
              <div style={{display:'flex',gap:6,justifyContent:'flex-end'}}>
                <button onClick={()=>handleValidateQSO(qso.id,false)} style={S.btnDanger}><XCircle style={{width:13,height:13}}/> Refuser</button>
                <button onClick={()=>handleValidateQSO(qso.id,true)} style={S.btnSuccess}><CheckCircle2 style={{width:13,height:13}}/> Valider</button>
              </div>
            </div>
          ))}

          {/* Sous-section : Toutes les sessions + créer */}
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,flexWrap:'wrap'}}>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'#6B7280',textTransform:'uppercase',letterSpacing:'0.08em'}}>
              Toutes les sessions ({allQSO.length})
            </div>
            <button onClick={()=>setShowQsoForm(!showQsoForm)} style={{...S.btnPrimary,fontSize:'0.75rem',padding:'6px 10px'}}>
              <Plus style={{width:12,height:12}}/> Nouvelle session
            </button>
          </div>

          {showQsoForm && (
            <form onSubmit={handleCreateQSO} style={{...S.card,padding:'16px',display:'flex',gap:12,alignItems:'flex-end',flexWrap:'wrap'}}>
              <div style={{flex:1,minWidth:140}}>
                <label style={S.label}>Date</label>
                <input type="date" required style={S.inp} value={qsoForm.date} onChange={e=>setQsoForm({...qsoForm,date:e.target.value})}/>
              </div>
              <div style={{width:110}}>
                <label style={S.label}>Heure</label>
                <input type="time" required style={S.inp} value={qsoForm.heure} onChange={e=>setQsoForm({...qsoForm,heure:e.target.value})}/>
              </div>
              <button type="submit" style={S.btnPrimary}>Créer</button>
              <button type="button" onClick={()=>setShowQsoForm(false)} style={S.btnGhost}>Annuler</button>
            </form>
          )}

          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            {allQSO.slice(0,20).map(qso => {
              const statColors = { libre:{bg:'#F3F4F6',c:'#6B7280'}, pending:{bg:'#FEF9C3',c:'#713F12'}, confirmed:{bg:'#D1FAE5',c:'#065F46'} };
              const statLabels = { libre:'Libre', pending:'En attente', confirmed:'Confirmé' };
              const sc = statColors[qso.statut] || statColors.libre;
              const nom = qso.capitaine_nom_libre || `${qso.capitaine_prenom||''} ${qso.capitaine_nom||''}`.trim();
              const indic = qso.capitaine_indicatif_libre || qso.capitaine_indicatif;
              return (
                <div key={qso.id} style={{...S.card,padding:'10px 12px',display:'flex',alignItems:'stretch',gap:10}}>
                  {/* Date */}
                  <div style={{width:58,flexShrink:0,display:'flex',flexDirection:'column',justifyContent:'center'}}>
                    <div style={{fontSize:'0.6rem',fontWeight:600,color:'#9CA3AF',textTransform:'uppercase'}}>{format(new Date(qso.date),'EEE',{locale:fr})}</div>
                    <div style={{fontSize:'0.8125rem',fontWeight:800,color:'#111827',lineHeight:1.2}}>{format(new Date(qso.date),'dd MMM',{locale:fr})}</div>
                    <div style={{fontSize:'0.6rem',color:'#9CA3AF'}}>{qso.heure?.substring(0,5)}</div>
                  </div>
                  {/* Contenu */}
                  <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',justifyContent:'center',gap:4}}>
                    {qso.statut === 'confirmed'
                      ? <div style={{fontSize:'0.8125rem',fontWeight:600,color:'#111827',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                          {nom} <span style={{fontFamily:'monospace',fontSize:'0.6875rem',color:'#6B7280',fontWeight:400}}>· {indic}</span>
                        </div>
                      : <div style={{fontSize:'0.8125rem',color:'#9CA3AF',fontStyle:'italic'}}>Sans capitaine</div>
                    }
                    <div style={{display:'flex',alignItems:'center',gap:5,flexWrap:'wrap'}}>
                      <span style={S.badge({backgroundColor:sc.bg,color:sc.c})}>{statLabels[qso.statut]||qso.statut}</span>
                      {qso.statut === 'confirmed' && (
                        <span onClick={()=>handleReleaseQSO(qso.id, qso.capitaine_nom_libre || qso.capitaine_prenom)} style={{...S.badge({backgroundColor:'#FFFBEB',color:'#D97706'}),border:'1px solid #FDE68A',cursor:'pointer'}}>Libérer</span>
                      )}
                    </div>
                  </div>
                  {/* Supprimer */}
                  <button onClick={()=>handleDeleteQSO(qso.id)} style={{background:'none',border:'none',cursor:'pointer',color:'#DC2626',padding:'4px',display:'flex',alignItems:'center',flexShrink:0,alignSelf:'center'}}>
                    <Trash2 style={{width:14,height:14}}/>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════
          TAB ANNONCES
      ══════════════════════════════════════════ */}
      {activeTab === 'annonces' && (
        <div style={{display:'flex',flexDirection:'column',gap:16}}>

          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,flexWrap:'wrap'}}>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'#6B7280',textTransform:'uppercase',letterSpacing:'0.08em'}}>
              En attente ({pendingAnnonces.length})
            </div>
            <button onClick={()=>setShowAnnonceForm(!showAnnonceForm)} style={{...S.btnPrimary,fontSize:'0.75rem',padding:'6px 10px'}}>
              <Plus style={{width:12,height:12}}/> Nouvelle annonce
            </button>
          </div>

          {showAnnonceForm && (
            <form onSubmit={handleCreateAnnonce} style={{...S.card,padding:'16px',display:'flex',flexDirection:'column',gap:12}}>
              <div style={{display:'flex',gap:6}}>
                {['Vente','Recherche','À donner'].map(t=>(
                  <button key={t} type="button" onClick={()=>setAnnonceForm({...annonceForm,type:t})}
                    style={{flex:1,padding:'6px',borderRadius:6,fontSize:'0.8125rem',fontWeight:600,cursor:'pointer',border:'1px solid',
                      backgroundColor: annonceForm.type===t ? '#111827' : 'white',
                      color: annonceForm.type===t ? 'white' : '#374151',
                      borderColor: annonceForm.type===t ? '#111827' : '#E5E7EB'}}>{t}</button>
                ))}
              </div>
              <div>
                <label style={S.label}>Titre *</label>
                <input required style={S.inp} value={annonceForm.titre} onChange={e=>setAnnonceForm({...annonceForm,titre:e.target.value})} placeholder="Titre de l'annonce"/>
              </div>
              <div>
                <label style={S.label}>Description *</label>
                <textarea required style={{...S.inp,minHeight:80,resize:'vertical'}} value={annonceForm.contenu} onChange={e=>setAnnonceForm({...annonceForm,contenu:e.target.value})} placeholder="Description…"/>
              </div>
              <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                <button type="button" onClick={()=>setShowAnnonceForm(false)} style={S.btnGhost}>Annuler</button>
                <button type="submit" style={S.btnPrimary}>Publier</button>
              </div>
            </form>
          )}

          {pendingAnnonces.length === 0 ? (
            <div style={{...S.card,padding:'24px',textAlign:'center',color:'#9CA3AF',fontSize:'0.875rem'}}>Aucune annonce en attente.</div>
          ) : pendingAnnonces.map(a => {
            const typeColors = { Vente:{bg:'#DBEAFE',c:'#1D4ED8'}, Recherche:{bg:'#EDE9FE',c:'#6D28D9'}, 'À donner':{bg:'#D1FAE5',c:'#065F46'} };
            const tc = typeColors[a.type] || {bg:'#F3F4F6',c:'#374151'};
            return (
              <div key={a.id} onClick={()=>setSelectedAnnonce(a)} style={{...S.card,padding:'14px 16px',borderLeft:'3px solid #F59E0B',cursor:'pointer',transition:'box-shadow 0.15s'}} onMouseEnter={e=>e.currentTarget.style.boxShadow='0 4px 12px rgba(0,0,0,0.1)'} onMouseLeave={e=>e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.06)'}>
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12,marginBottom:8}}>
                  <div style={{flex:1}}>
                    <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:4,flexWrap:'wrap'}}>
                      <span style={S.badge({backgroundColor:tc.bg,color:tc.c})}>{a.type}</span>
                      {a.etat && <span style={S.badge({backgroundColor:'#F3F4F6',color:'#374151'})}>{a.etat}</span>}
                      {a.categorie && <span style={{fontSize:'0.75rem',color:'#9CA3AF'}}>{a.categorie}</span>}
                    </div>
                    <div style={{fontWeight:700,fontSize:'0.9375rem',color:'#111827',marginBottom:2}}>{a.titre}</div>
                    <div style={{fontSize:'0.8125rem',color:'#6B7280',display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                      <span>Par <strong>{a.auteur_prenom} {a.auteur_nom}</strong> · <span style={{fontFamily:'monospace'}}>{a.auteur_indicatif}</span></span>
                      {(a.marque||a.modele) && <span style={{color:'#374151',fontWeight:500}}>{[a.marque,a.modele].filter(Boolean).join(' ')}</span>}
                      {a.localisation && <span>📍 {a.localisation}</span>}
                      {a.prix && <span style={{fontWeight:700,color:'#111827'}}>{Number(a.prix).toLocaleString('fr-CH')} {a.devise||'CHF'}</span>}
                    </div>
                  </div>
                  <span style={S.badge({backgroundColor:'#FEF9C3',color:'#713F12'})}><Clock style={{width:10,height:10}}/> En attente</span>
                </div>
                <div style={{backgroundColor:'#F9FAFB',borderRadius:6,padding:'8px 12px',marginBottom:4,fontSize:'0.8125rem',color:'#374151',lineHeight:1.5,maxHeight:expandedDesc[`a-${a.id}`] ? 'none' : 60,overflow:'hidden',wordBreak:'break-word',overflowWrap:'anywhere'}}>{a.contenu}</div>
                <button onClick={e=>{e.stopPropagation();setExpandedDesc(p=>({...p,[`a-${a.id}`]:!p[`a-${a.id}`]}));}} style={{background:'none',border:'none',cursor:'pointer',fontSize:'0.7rem',color:'#6B7280',padding:'0 0 8px',display:'flex',alignItems:'center',gap:3}}>
                  {expandedDesc[`a-${a.id}`] ? '▲ Réduire' : '▼ Voir tout'}
                </button>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
                  <span style={{fontSize:'0.75rem',color:'#9CA3AF',fontStyle:'italic'}}>Cliquer pour voir le détail complet</span>
                  <div style={{display:'flex',gap:6}}>
                    <button onClick={e=>{e.stopPropagation();handleValidateAnnonce(a.id,false);}} style={S.btnDanger}><XCircle style={{width:13,height:13}}/> Refuser</button>
                    <button onClick={e=>{e.stopPropagation();handleValidateAnnonce(a.id,true);}} style={S.btnSuccess}><CheckCircle2 style={{width:13,height:13}}/> Approuver</button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* ── Toutes les annonces publiées ── */}
          <div style={{display:'flex',alignItems:'center',gap:8,marginTop:4}}>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'#6B7280',textTransform:'uppercase',letterSpacing:'0.08em'}}>
              Toutes les annonces ({allAnnonces.filter(a=>a.statut==='published').length})
            </div>
            <div style={{flex:1,height:1,backgroundColor:'#F3F4F6'}}/>
          </div>

          {allAnnonces.filter(a=>a.statut==='published').length === 0 ? (
            <div style={{...S.card,padding:'24px',textAlign:'center',color:'#9CA3AF',fontSize:'0.875rem'}}>Aucune annonce publiée.</div>
          ) : allAnnonces.filter(a=>a.statut==='published').map(a => {
            const typeColors = { Vente:{bg:'#DBEAFE',c:'#1D4ED8'}, Recherche:{bg:'#EDE9FE',c:'#6D28D9'}, 'À donner':{bg:'#D1FAE5',c:'#065F46'} };
            const tc = typeColors[a.type] || {bg:'#F3F4F6',c:'#374151'};
            return (
              <div key={a.id} style={{...S.card,padding:'10px 14px',display:'flex',alignItems:'center',gap:10}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap',marginBottom:2}}>
                    <span style={S.badge({backgroundColor:tc.bg,color:tc.c})}>{a.type}</span>
                    <span style={{fontWeight:700,fontSize:'0.875rem',color:'#111827',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.titre}</span>
                  </div>
                  <div style={{fontSize:'0.75rem',color:'#9CA3AF',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    <span style={{fontFamily:'monospace',fontWeight:600,color:'#6B7280'}}>{a.auteur_indicatif}</span>
                    <span style={{margin:'0 4px'}}>·</span>{a.auteur_prenom} {a.auteur_nom}
                  </div>
                </div>
                <div style={{display:'flex',gap:5,flexShrink:0,alignItems:'center'}}>
                  <span style={S.badge({backgroundColor:'#D1FAE5',color:'#065F46'})}>Publiée</span>
                  <button onClick={()=>handleValidateAnnonce(a.id,false)} style={{...S.btnDanger,padding:'3px 9px',fontSize:'0.7rem'}}>
                    <XCircle style={{width:12,height:12}}/> Retirer
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ══════════════════════════════════════════
          TAB AGENDA
      ══════════════════════════════════════════ */}
      {activeTab === 'agenda' && (
        <div style={{display:'flex',flexDirection:'column',gap:16}}>

          {/* Validations en attente */}
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'#6B7280',textTransform:'uppercase',letterSpacing:'0.08em'}}>
              En attente de validation ({pendingEvenements.length})
            </div>
            <div style={{flex:1,height:1,backgroundColor:'#F3F4F6'}}/>
          </div>
          {pendingEvenements.length === 0 ? (
            <div style={{...S.card,padding:'20px',textAlign:'center',color:'#9CA3AF',fontSize:'0.875rem'}}>Aucune proposition en attente.</div>
          ) : pendingEvenements.map(ev => (
                <div key={ev.id} style={{...S.card,padding:'14px 16px',borderLeft:'3px solid #F59E0B'}}>
                  <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12,marginBottom:8}}>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:'0.9375rem',color:'#111827',marginBottom:2}}>{ev.titre}</div>
                      <div style={{fontSize:'0.8125rem',color:'#6B7280'}}>
                        {format(new Date(ev.date_debut),'dd MMM yyyy à HH:mm',{locale:fr})} · {ev.lieu}
                      </div>
                      <div style={{fontSize:'0.8125rem',color:'#6B7280',marginTop:2}}>
                        Par {ev.auteur_prenom} {ev.auteur_nom} ({ev.auteur_indicatif})
                      </div>
                    </div>
                    <span style={S.badge({backgroundColor:'#FEF9C3',color:'#713F12'})}><Clock style={{width:10,height:10}}/> En attente</span>
                  </div>
                  <div style={{backgroundColor:'#F9FAFB',borderRadius:6,padding:'8px 12px',marginBottom:4,fontSize:'0.8125rem',color:'#374151',lineHeight:1.5,maxHeight:expandedDesc[`ev-${ev.id}`] ? 'none' : 60,overflow:'hidden',wordBreak:'break-word',overflowWrap:'anywhere'}}>{ev.description}</div>
                  <button onClick={()=>setExpandedDesc(p=>({...p,[`ev-${ev.id}`]:!p[`ev-${ev.id}`]}))} style={{background:'none',border:'none',cursor:'pointer',fontSize:'0.7rem',color:'#6B7280',padding:'0 0 8px',display:'flex',alignItems:'center',gap:3}}>
                    {expandedDesc[`ev-${ev.id}`] ? '▲ Réduire' : '▼ Voir tout'}
                  </button>
                  <div style={{display:'flex',gap:6,justifyContent:'flex-end'}}>
                    <button onClick={()=>handleValidateEvenement(ev.id,false)} style={S.btnDanger}><XCircle style={{width:13,height:13}}/> Refuser</button>
                    <button onClick={()=>handleValidateEvenement(ev.id,true)} style={S.btnSuccess}><CheckCircle2 style={{width:13,height:13}}/> Approuver</button>
                  </div>
                </div>
          ))}

          {/* Tous les événements + créer */}
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,flexWrap:'wrap'}}>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'#6B7280',textTransform:'uppercase',letterSpacing:'0.08em'}}>
              Événements ({allEvenements.length})
            </div>
            <button onClick={()=>{ setEditEvenement(null); setEvenementForm({titre:'',description:'',debut_date:'',debut_heure:'',fin_date:'',fin_heure:'',lieu:'',lien:'',type:'QSO'}); setShowEvenementForm(!showEvenementForm); }} style={{...S.btnPrimary,fontSize:'0.75rem',padding:'6px 10px'}}>
              <Plus style={{width:12,height:12}}/> Nouvel événement
            </button>
          </div>

          {showEvenementForm && (
            <form onSubmit={handleSaveEvenement} style={{...S.card,padding:'16px',display:'flex',flexDirection:'column',gap:12}}>
              <div style={{fontWeight:700,fontSize:'0.9375rem',color:'#111827',marginBottom:4}}>
                {editEvenement ? 'Modifier l\'événement' : 'Créer un événement'}
              </div>
              <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                {['QSO','Stamm','Concours','Assemblée','Autre'].map(t => {
                  const typeColors = { QSO:{bg:'#DBEAFE',c:'#1D4ED8'}, Stamm:{bg:'#D1FAE5',c:'#065F46'}, Concours:{bg:'#FEE2E2',c:'#991B1B'}, Assemblée:{bg:'#EDE9FE',c:'#6D28D9'}, Autre:{bg:'#F3F4F6',c:'#374151'} };
                  const active = (evenementForm.type || 'QSO') === t;
                  const tag = typeColors[t] || typeColors.Autre;
                  return <button key={t} type="button" onClick={()=>setEvenementForm({...evenementForm,type:t})} style={{padding:'3px 9px',borderRadius:5,fontSize:'0.75rem',fontWeight:600,cursor:'pointer',border:'1px solid',backgroundColor:active?tag.bg:'white',color:active?tag.c:'#6B7280',borderColor:active?tag.c:'#E5E7EB'}}>{t}</button>;
                })}
              </div>
              <div>
                <label style={S.label}>Titre *</label>
                <input required style={S.inp} value={evenementForm.titre} onChange={e=>setEvenementForm({...evenementForm,titre:e.target.value})} placeholder="Titre de l'événement"/>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div>
                  <label style={S.label}>Date début *</label>
                  <input type="date" required style={S.inp} value={evenementForm.debut_date||''} onChange={e=>setEvenementForm({...evenementForm,debut_date:e.target.value})}/>
                </div>
                <div>
                  <label style={S.label}>Heure début *</label>
                  <input type="time" required style={S.inp} value={evenementForm.debut_heure||''} onChange={e=>setEvenementForm({...evenementForm,debut_heure:e.target.value})}/>
                </div>
                <div>
                  <label style={S.label}>Date fin <span style={{fontWeight:400,color:'#9CA3AF'}}>(opt.)</span></label>
                  <input type="date" style={S.inp} value={evenementForm.fin_date||''} onChange={e=>setEvenementForm({...evenementForm,fin_date:e.target.value})}/>
                </div>
                <div>
                  <label style={S.label}>Heure fin</label>
                  <input type="time" style={S.inp} value={evenementForm.fin_heure||''} onChange={e=>setEvenementForm({...evenementForm,fin_heure:e.target.value})}/>
                </div>
                <div style={{gridColumn:'1/-1'}}>
                  <label style={S.label}>Lieu *</label>
                  <input required style={S.inp} value={evenementForm.lieu} onChange={e=>setEvenementForm({...evenementForm,lieu:e.target.value})} placeholder="Lieu de l'événement"/>
                </div>
              </div>
              <div>
                <label style={S.label}>Description *</label>
                <textarea required style={{...S.inp,minHeight:80,resize:'vertical'}} value={evenementForm.description} onChange={e=>setEvenementForm({...evenementForm,description:e.target.value})} placeholder="Description…"/>
              </div>
              <div>
                <label style={S.label}>Lien externe (optionnel)</label>
                <input type="url" style={S.inp} value={evenementForm.lien} onChange={e=>setEvenementForm({...evenementForm,lien:e.target.value})} placeholder="https://…"/>
              </div>
              <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                <button type="button" onClick={()=>{setShowEvenementForm(false);setEditEvenement(null);}} style={S.btnGhost}>Annuler</button>
                <button type="submit" style={S.btnPrimary}>{editEvenement ? 'Enregistrer' : 'Créer'}</button>
              </div>
            </form>
          )}

          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {allEvenements.length === 0 && (
              <div style={{...S.card,padding:'24px',textAlign:'center',color:'#9CA3AF',fontSize:'0.875rem'}}>Aucun événement.</div>
            )}
            {allEvenements.map(ev => {
              const statusColors = { pending:{bg:'#FEF9C3',c:'#713F12'}, published:{bg:'#D1FAE5',c:'#065F46'}, rejected:{bg:'#FEE2E2',c:'#991B1B'} };
              const statusLabels = { pending:'En attente', published:'Publié', rejected:'Refusé' };
              const sc = statusColors[ev.statut] || statusColors.pending;
              return (
                <div key={ev.id} style={{...S.card,padding:'10px 12px',display:'flex',alignItems:'center',gap:8}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:600,fontSize:'0.8125rem',color:'#111827',marginBottom:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ev.titre}</div>
                    <div style={{fontSize:'0.6875rem',color:'#6B7280',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {format(new Date(ev.date_debut),'dd MMM yyyy',{locale:fr})}{ev.lieu ? ` · ${ev.lieu}` : ''}
                    </div>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
                    <span style={S.badge({backgroundColor:sc.bg,color:sc.c})}>{statusLabels[ev.statut]||ev.statut}</span>
                    <button onClick={()=>openEditEvenement(ev)} style={{...S.btnGhost,padding:'3px 8px',fontSize:'0.75rem'}}>Éditer</button>
                    <button onClick={()=>handleDeleteEvenement(ev.id)} style={{background:'none',border:'none',cursor:'pointer',color:'#DC2626',padding:4,display:'flex'}}>
                      <Trash2 style={{width:14,height:14}}/>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════
          TAB STAMM
      ══════════════════════════════════════════ */}
      {activeTab === 'stamm' && (
        <div style={{display:'flex',flexDirection:'column',gap:16}}>

          {/* Header + Créer */}
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:8}}>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'#6B7280',textTransform:'uppercase',letterSpacing:'0.08em'}}>
              Sessions Stamm ({stammSessions.length})
            </div>
            <button onClick={()=>setShowStammForm(!showStammForm)} style={{...S.btnPrimary,fontSize:'0.75rem',padding:'6px 10px'}}>
              <Plus style={{width:12,height:12}}/> Nouvelle session
            </button>
          </div>

          {/* Formulaire création */}
          {showStammForm && (
            <form onSubmit={handleCreateStamm} style={{...S.card,padding:'16px',display:'flex',flexDirection:'column',gap:12}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 100px',gap:8}}>
                <div>
                  <label style={S.label}>Date *</label>
                  <input required type="date" style={S.inp} value={stammForm.date} onChange={e=>setStammForm({...stammForm,date:e.target.value})}/>
                </div>
                <div>
                  <label style={S.label}>Heure</label>
                  <input type="time" style={S.inp} value={stammForm.heure} onChange={e=>setStammForm({...stammForm,heure:e.target.value})}/>
                </div>
              </div>
              <div>
                <label style={S.label}>Description / note</label>
                <input style={S.inp} value={stammForm.description} onChange={e=>setStammForm({...stammForm,description:e.target.value})} placeholder="Soirée spéciale, invité…"/>
              </div>
              <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                <button type="button" onClick={()=>setShowStammForm(false)} style={S.btnGhost}>Annuler</button>
                <button type="submit" style={S.btnPrimary}>Créer</button>
              </div>
            </form>
          )}

          {/* Candidatures en attente */}
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'#6B7280',textTransform:'uppercase',letterSpacing:'0.08em'}}>
              Candidatures en attente ({stammSessions.filter(s=>s.statut==='pending').length})
            </div>
            <div style={{flex:1,height:1,backgroundColor:'#F3F4F6'}}/>
          </div>
          {stammSessions.filter(s => s.statut === 'pending').length === 0 ? (
            <div style={{...S.card,padding:'20px',textAlign:'center',color:'#9CA3AF',fontSize:'0.875rem'}}>Aucune candidature en attente.</div>
          ) : stammSessions.filter(s => s.statut === 'pending').map(s => {
            const ind = s.responsable_indicatif || s.responsable_ind_libre || s.responsable_nom_libre;
            return (
              <div key={s.id} style={{...S.card,padding:'8px 10px',borderLeft:'3px solid #F59E0B',display:'flex',alignItems:'center',gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:'0.8125rem',color:'#111827'}}>
                    {format(new Date(s.date),'EEE d MMM',{locale:fr})} · {s.heure?.slice(0,5)}
                  </div>
                  {ind && <div style={{display:'inline-flex',alignItems:'center',gap:4,backgroundColor:'#FEF9C3',border:'1px solid #FDE68A',borderRadius:6,padding:'1px 7px',color:'#92400E',fontWeight:600,fontSize:'0.75rem',marginTop:3}}>
                    🔑 <span style={{fontFamily:'monospace',fontWeight:700}}>{ind}</span>
                  </div>}
                </div>
                <div style={{display:'flex',gap:5,flexShrink:0,alignItems:'center'}}>
                  <button onClick={()=>handleValiderCandidature(s.id, true)} style={{...S.btnSuccess,minWidth:80,justifyContent:'center'}}>
                    <CheckCircle2 style={{width:12,height:12}}/> Confirmer
                  </button>
                  <button onClick={()=>handleValiderCandidature(s.id, false)} style={{background:'none',border:'none',cursor:'pointer',color:'#DC2626',padding:'4px',display:'flex',borderRadius:6}} title="Refuser">
                    <XCircle style={{width:16,height:16}}/>
                  </button>
                </div>
              </div>
            );
          })}

          {stammSessions.filter(s => s.statut !== 'pending').length === 0 ? (
            <div style={{...S.card,padding:'24px',textAlign:'center',color:'#9CA3AF',fontSize:'0.875rem'}}>Aucune session.</div>
          ) : stammSessions.filter(s => s.statut !== 'pending').map(s => {
            const annule = s.statut === 'annule';
            const confirmed = s.statut === 'confirmed';
            const pending = s.statut === 'pending';
            const libre = s.statut === 'libre';
            const ind = s.responsable_indicatif || s.responsable_ind_libre || s.responsable_nom_libre;
            const borderC = annule ? '#F87171' : confirmed ? '#10B981' : pending ? '#F59E0B' : '#E5E7EB';
            const statusBadge = annule
              ? S.badge({backgroundColor:'#FEE2E2',color:'#DC2626'})
              : confirmed ? S.badge({backgroundColor:'#D1FAE5',color:'#065F46'})
              : pending ? S.badge({backgroundColor:'#FEF9C3',color:'#713F12'})
              : S.badge({backgroundColor:'#F3F4F6',color:'#6B7280'});
            const statusLabel = annule ? 'Annulé' : confirmed ? 'Confirmé' : pending ? 'En attente' : 'Libre';
            return (
              <div key={s.id} style={{...S.card,borderLeft:`3px solid ${borderC}`,padding:'8px 10px',display:'flex',alignItems:'flex-start',gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <span style={{fontWeight:600,fontSize:'0.8125rem',color:annule?'#9CA3AF':'#111827',textDecoration:annule?'line-through':'none'}}>
                    {format(new Date(s.date),'EEE d MMM',{locale:fr})} · {s.heure?.slice(0,5)}
                  </span>
                  {ind && (
                    <div style={{fontSize:'0.75rem',color:'#6B7280',display:'inline-flex',alignItems:'center',gap:4,marginTop:2}}>
                      🔑 <span style={{fontFamily:'monospace',fontWeight:700,color:'#374151'}}>{ind}</span>
                    </div>
                  )}
                  {annule && s.annule_raison && <div style={{fontSize:'0.6875rem',color:'#DC2626',marginTop:1}}>{s.annule_raison}</div>}
                </div>
                <div style={{display:'flex',gap:5,flexShrink:0,alignItems:'center',alignSelf:'center'}}>
                  <span style={statusBadge}>{statusLabel}</span>
                  {annule
                    ? <button onClick={()=>handleRouvrirSession(s.id)} style={{...S.btnSuccess,minWidth:80,justifyContent:'center'}}><CheckCircle2 style={{width:12,height:12}}/> Réouvrir</button>
                    : <button onClick={()=>{setAnnulModal({session:s});setAnnulRaison('');}} style={{...S.btnDanger,minWidth:80,justifyContent:'center'}}><XCircle style={{width:12,height:12}}/> Annuler</button>
                  }
                  <button onClick={()=>handleDeleteStamm(s.id)} style={{background:'none',border:'none',cursor:'pointer',color:'#DC2626',padding:'3px',display:'flex',borderRadius:6}}>
                    <Trash2 style={{width:13,height:13}}/>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal annulation session */}
      {annulModal && (
        <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={()=>setAnnulModal(null)}>
          <div style={{backgroundColor:'white',borderRadius:12,padding:'24px',maxWidth:420,width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.22)'}} onClick={e=>e.stopPropagation()}>
            <div style={{fontWeight:800,fontSize:'1rem',color:'#111827',marginBottom:4}}>Annuler la session</div>
            <div style={{fontSize:'0.8125rem',color:'#6366F1',fontWeight:600,marginBottom:16}}>
              {format(new Date(annulModal.session.date),'EEEE d MMMM yyyy',{locale:fr})}
            </div>
            <div style={{marginBottom:18}}>
              <label style={{...S.label}}>Motif d'annulation *</label>
              <input required style={S.inp} value={annulRaison} onChange={e=>setAnnulRaison(e.target.value)}
                placeholder="Ex: Vacances, cours RA, fête nationale…"/>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button onClick={()=>setAnnulModal(null)} style={S.btnGhost}>Retour</button>
              <button onClick={handleAnnulerSession} disabled={!annulRaison.trim()} style={{...S.btnDanger,opacity:annulRaison.trim()?1:0.5}}>
                <XCircle style={{width:13,height:13}}/> Confirmer l'annulation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════
          TAB UTILISATEURS
      ══════════════════════════════════════════ */}
      {activeTab === 'users' && (
        <div style={{display:'flex',flexDirection:'column',gap:16}}>

          {!isAdminRole ? (
            <div style={{...S.card,padding:'32px',textAlign:'center',color:'#9CA3AF'}}>
              <Lock style={{width:28,height:28,margin:'0 auto 10px',display:'block',color:'#D1D5DB'}}/>
              <div style={{fontWeight:600,color:'#374151',marginBottom:4}}>Accès restreint</div>
              <div style={{fontSize:'0.875rem'}}>La gestion des membres est réservée aux administrateurs.</div>
            </div>
          ) : ['admin','comite','membre'].map(roleKey => {
            const group = users.filter(u => u.role === roleKey);
            if (group.length === 0) return null;
            const rs = ROLE_STYLES[roleKey];
            return (
              <div key={roleKey}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
                  <span style={{fontSize:'0.6875rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.08em',color:rs.c}}>
                    {roleKey === 'comite' ? 'Comité' : roleKey === 'admin' ? 'Administrateurs' : 'Membres'}
                  </span>
                  <span style={{fontSize:'0.6875rem',color:'#9CA3AF',fontWeight:500}}>({group.length})</span>
                  <div style={{flex:1,height:1,backgroundColor:'#F3F4F6'}}/>
                </div>
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {group.map(u => (
                    <div key={u.id} style={{
                      ...S.card, padding:'10px 12px',
                      borderLeft: `3px solid ${u.disabled_at ? '#FCA5A5' : rs.border}`,
                      opacity: u.disabled_at ? 0.7 : 1,
                      backgroundColor: u.disabled_at ? '#FFF8F8' : 'white',
                    }}>
                      {/* Ligne 1 : avatar + nom + badges + boutons */}
                      <div style={{display:'flex',alignItems:'center',gap:8,minWidth:0}}>
                        <div style={{
                          width:34,height:34,borderRadius:'50%',flexShrink:0,
                          backgroundColor: u.disabled_at ? '#FEE2E2' : rs.avatarBg,
                          border:`1px solid ${u.disabled_at ? '#FCA5A5' : rs.border}`,
                          display:'flex',alignItems:'center',justifyContent:'center',
                        }}>
                          <span style={{fontSize:'0.8rem',fontWeight:800,color: u.disabled_at ? '#EF4444' : rs.avatarC}}>
                            {u.prenom?.[0]}{u.nom?.[0]}
                          </span>
                        </div>
                        <div style={{flex:1,minWidth:0,overflow:'hidden'}}>
                          <div style={{display:'flex',alignItems:'center',gap:5,flexWrap:'nowrap',overflow:'hidden'}}>
                            <span style={{fontWeight:700,fontSize:'0.875rem',color: u.disabled_at ? '#9CA3AF' : '#111827',textDecoration: u.disabled_at ? 'line-through' : 'none',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{u.prenom} {u.nom}</span>
                            {!u.verified_at && <span style={{fontSize:'0.55rem',fontWeight:700,padding:'1px 4px',borderRadius:3,backgroundColor:'#FEF9C3',color:'#713F12',letterSpacing:'0.04em',flexShrink:0,whiteSpace:'nowrap'}}>NON VÉR.</span>}
                            {u.disabled_at && <span style={{fontSize:'0.55rem',fontWeight:700,padding:'1px 4px',borderRadius:3,backgroundColor:'#FEE2E2',color:'#DC2626',letterSpacing:'0.04em',flexShrink:0,whiteSpace:'nowrap'}}>DÉSACT.</span>}
                          </div>
                          <div style={{fontSize:'0.7rem',color:'#9CA3AF',marginTop:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                            <span style={{fontFamily:'monospace',fontWeight:700,color:'#6B7280'}}>{u.indicatif}</span>
                            <span style={{margin:'0 4px'}}>·</span>{u.email}
                          </div>
                        </div>
                        {isAdminRole ? (
                          <div style={{display:'flex',alignItems:'center',gap:5,flexShrink:0}}>
                            <div style={{position:'relative',display:'inline-block'}}>
                              <button
                                type="button"
                                disabled={!!u.disabled_at}
                                onClick={e=>{e.stopPropagation(); setOpenRoleDropdown(openRoleDropdown===u.id ? null : u.id);}}
                                style={{
                                  display:'inline-flex', alignItems:'center', gap:5,
                                  border:`1px solid ${rs.border}`, borderRadius:20,
                                  backgroundColor:rs.bg, color:rs.c,
                                  fontWeight:700, fontSize:'0.75rem', padding:'5px 10px',
                                  cursor: u.disabled_at ? 'not-allowed' : 'pointer', outline:'none',
                                  opacity: u.disabled_at ? 0.5 : 1,
                                }}
                              >
                                {rs.label}<ChevronDown style={{width:11,height:11}}/>
                              </button>
                              {openRoleDropdown === u.id && (
                                <>
                                  <div style={{position:'fixed',inset:0,zIndex:300}} onClick={()=>setOpenRoleDropdown(null)}/>
                                  <div style={{position:'absolute',right:0,top:'calc(100% + 4px)',backgroundColor:'white',border:'1px solid #E5E7EB',borderRadius:8,boxShadow:'0 8px 24px rgba(0,0,0,0.12)',zIndex:301,minWidth:110,overflow:'hidden'}}>
                                    {[{v:'membre',l:'Membre'},{v:'comite',l:'Comité'},{v:'admin',l:'Admin'}].map(opt=>(
                                      <button key={opt.v} type="button"
                                        onClick={()=>{ handleUpdateRole(u.id, opt.v); setOpenRoleDropdown(null); }}
                                        style={{display:'flex',alignItems:'center',gap:8,width:'100%',padding:'8px 14px',border:'none',backgroundColor: u.role===opt.v ? '#F3F4F6' : 'white',color: u.role===opt.v ? '#111827' : '#374151',fontSize:'0.8125rem',fontWeight: u.role===opt.v ? 700 : 500,cursor:'pointer',textAlign:'left'}}>
                                        {u.role===opt.v && <CheckCircle2 style={{width:12,height:12,color:'#059669',flexShrink:0}}/>}
                                        {u.role!==opt.v && <span style={{width:12,flexShrink:0}}/>}
                                        {opt.l}
                                      </button>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                            {u.disabled_at ? (
                              <button onClick={()=>handleDisableUser(u.id, false)} style={{...S.btnSuccess,padding:'3px 9px',fontSize:'0.7rem'}}>
                                <UserCheck style={{width:11,height:11}}/>
                              </button>
                            ) : (
                              <button onClick={()=>handleDisableUser(u.id, true)} style={{...S.btnGhost,padding:'3px 9px',fontSize:'0.7rem',color:'#D97706',borderColor:'#FDE68A'}}>
                                <UserX style={{width:11,height:11}}/>
                              </button>
                            )}
                            <button
                              onClick={()=>setConfirmDelete({id:u.id, name:`${u.prenom} ${u.nom}`})}
                              style={{...S.btnDanger,padding:'3px 9px',fontSize:'0.7rem'}}
                            >
                              <Trash2 style={{width:11,height:11}}/>
                            </button>
                          </div>
                        ) : (
                          <span style={{...S.badge({backgroundColor:rs.bg,color:rs.c,border:`1px solid ${rs.border}`}),flexShrink:0}}>
                            {rs.label}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modal confirmation suppression compte ── */}
      {confirmDelete && (
        <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.55)',zIndex:1100,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}} onClick={()=>setConfirmDelete(null)}>
          <div style={{backgroundColor:'white',borderRadius:14,width:'100%',maxWidth:420,padding:'28px',boxShadow:'0 24px 60px rgba(0,0,0,0.25)'}} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',alignItems:'flex-start',gap:14,marginBottom:20}}>
              <div style={{width:44,height:44,borderRadius:10,backgroundColor:'#FEF2F2',border:'1px solid #FECACA',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <AlertTriangle style={{width:22,height:22,color:'#DC2626'}}/>
              </div>
              <div>
                <div style={{fontWeight:700,fontSize:'1rem',color:'#111827',marginBottom:4}}>Supprimer ce compte ?</div>
                <div style={{fontSize:'0.875rem',color:'#6B7280',lineHeight:1.5}}>
                  Le compte de <strong style={{color:'#374151'}}>{confirmDelete.name}</strong> sera définitivement supprimé, ainsi que toutes ses annonces, événements et inscriptions Stamm. Les sessions QSO seront libérées.
                </div>
                <div style={{marginTop:10,padding:'8px 12px',backgroundColor:'#FEF2F2',borderRadius:6,fontSize:'0.8125rem',color:'#DC2626',fontWeight:500}}>
                  Cette action est irréversible.
                </div>
              </div>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button onClick={()=>setConfirmDelete(null)} style={{...S.btnGhost,padding:'8px 16px'}}>Annuler</button>
              <button
                onClick={()=>handleDeleteUser(confirmDelete.id)}
                style={{...S.btnDanger,padding:'8px 16px',backgroundColor:'#DC2626',color:'white',borderColor:'#DC2626'}}
              >
                <Trash2 style={{width:14,height:14}}/> Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal détail annonce ── */}
      {selectedAnnonce && (() => {
        const a = selectedAnnonce;
        const typeColors = { Vente:{bg:'#DBEAFE',c:'#1D4ED8',border:'#BFDBFE'}, Recherche:{bg:'#EDE9FE',c:'#6D28D9',border:'#DDD6FE'}, 'À donner':{bg:'#D1FAE5',c:'#065F46',border:'#A7F3D0'} };
        const tc = typeColors[a.type] || {bg:'#F3F4F6',c:'#374151',border:'#E5E7EB'};
        const images = (() => { try { return Array.isArray(a.images) ? a.images : JSON.parse(a.images || '[]'); } catch { return []; } })();
        return (
          <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.6)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}} onClick={()=>setSelectedAnnonce(null)}>
            <div style={{backgroundColor:'white',borderRadius:16,width:'100%',maxWidth:780,maxHeight:'92vh',overflow:'hidden',display:'flex',flexDirection:'column',boxShadow:'0 32px 80px rgba(0,0,0,0.3)'}} onClick={e=>e.stopPropagation()}>

              {/* ── Photos pleine largeur ── */}
              {images.length > 0 ? (
                <div style={{position:'relative',height:260,flexShrink:0,backgroundColor:'#111827',overflow:'hidden',borderRadius:'16px 16px 0 0'}}>
                  <img src={`${API_BASE}${images[0]}`} alt="" style={{width:'100%',height:'100%',objectFit:'cover',opacity:0.92}}/>
                  <div style={{position:'absolute',inset:0,background:'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.55) 100%)'}}/>
                  {/* Vignettes */}
                  {images.length > 1 && (
                    <div style={{position:'absolute',bottom:12,right:12,display:'flex',gap:6}}>
                      {images.map((url,i)=>(
                        <div key={i} style={{width:52,height:52,borderRadius:8,overflow:'hidden',border:`2px solid ${i===0?'white':'rgba(255,255,255,0.5)'}`,flexShrink:0}}>
                          <img src={`${API_BASE}${url}`} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Badges flottants */}
                  <div style={{position:'absolute',top:14,left:16,display:'flex',gap:7,alignItems:'center'}}>
                    <span style={{backgroundColor:tc.bg,color:tc.c,fontSize:'0.75rem',fontWeight:800,padding:'4px 12px',borderRadius:20,border:`1px solid ${tc.border}`}}>{a.type}</span>
                    {a.etat && <span style={{backgroundColor:'rgba(255,255,255,0.92)',color:'#374151',fontSize:'0.75rem',fontWeight:700,padding:'4px 10px',borderRadius:20}}>{a.etat}</span>}
                    <span style={{backgroundColor:'#FEF9C3',color:'#713F12',fontSize:'0.75rem',fontWeight:700,padding:'4px 10px',borderRadius:20,display:'flex',alignItems:'center',gap:4}}><Clock style={{width:10,height:10}}/> En attente</span>
                  </div>
                  <button onClick={()=>setSelectedAnnonce(null)} style={{position:'absolute',top:12,right:12,width:32,height:32,borderRadius:'50%',background:'rgba(0,0,0,0.5)',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(4px)'}}><X style={{width:16,height:16,color:'white'}}/></button>
                </div>
              ) : (
                /* Header sans photo */
                <div style={{padding:'18px 22px',borderBottom:'1px solid #F3F4F6',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                    <span style={S.badge({backgroundColor:tc.bg,color:tc.c,border:`1px solid ${tc.border}`})}>{a.type}</span>
                    {a.etat && <span style={S.badge({backgroundColor:'#F3F4F6',color:'#374151'})}>{a.etat}</span>}
                    <span style={S.badge({backgroundColor:'#FEF9C3',color:'#713F12'})}><Clock style={{width:10,height:10}}/> En attente</span>
                  </div>
                  <button onClick={()=>setSelectedAnnonce(null)} style={{background:'none',border:'none',cursor:'pointer',color:'#9CA3AF',padding:4,display:'flex',borderRadius:6}}><X style={{width:18,height:18}}/></button>
                </div>
              )}

              {/* ── Corps scrollable ── */}
              <div style={{overflowY:'auto',flex:1,padding:'24px 26px',display:'flex',flexDirection:'column',gap:20}}>

                {/* Titre + prix */}
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:16,flexWrap:'wrap'}}>
                  <div style={{flex:1}}>
                    <h2 style={{margin:'0 0 8px',fontSize:'1.375rem',fontWeight:800,color:'#111827',lineHeight:1.2}}>{a.titre}</h2>
                    <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',fontSize:'0.8125rem',color:'#6B7280'}}>
                      <span>Par <strong style={{color:'#374151'}}>{a.auteur_prenom} {a.auteur_nom}</strong></span>
                      <span style={{fontFamily:'monospace',fontWeight:700,color:'#6366F1',backgroundColor:'#EEF2FF',padding:'1px 7px',borderRadius:5,fontSize:'0.75rem'}}>{a.auteur_indicatif}</span>
                      {a.localisation && <span style={{display:'flex',alignItems:'center',gap:3}}><MapPin style={{width:12,height:12,color:'#9CA3AF'}}/>{a.localisation}</span>}
                    </div>
                  </div>
                  {a.prix && (
                    <div style={{textAlign:'right',flexShrink:0}}>
                      <div style={{fontSize:'1.75rem',fontWeight:900,color:'#111827',lineHeight:1}}>{Number(a.prix).toLocaleString('fr-CH')}</div>
                      <div style={{fontSize:'0.875rem',fontWeight:600,color:'#9CA3AF'}}>{a.devise||'CHF'}</div>
                    </div>
                  )}
                </div>

                {/* Méta pills */}
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  {a.categorie && <div style={{display:'flex',alignItems:'center',gap:6,backgroundColor:'#F9FAFB',border:'1px solid #E5E7EB',borderRadius:8,padding:'6px 12px',fontSize:'0.8125rem',color:'#374151',fontWeight:500}}><Package style={{width:13,height:13,color:'#9CA3AF'}}/>{a.categorie}</div>}
                  {(a.marque||a.modele) && <div style={{display:'flex',alignItems:'center',gap:6,backgroundColor:'#F9FAFB',border:'1px solid #E5E7EB',borderRadius:8,padding:'6px 12px',fontSize:'0.8125rem',color:'#374151',fontWeight:500}}><Tag style={{width:13,height:13,color:'#9CA3AF'}}/>{[a.marque,a.modele].filter(Boolean).join(' · ')}</div>}
                  {a.duree_mois && <div style={{display:'flex',alignItems:'center',gap:6,backgroundColor:'#EEF2FF',border:'1px solid #C7D2FE',borderRadius:8,padding:'6px 12px',fontSize:'0.8125rem',color:'#4F46E5',fontWeight:600}}><Clock style={{width:13,height:13}}/>{a.duree_mois} mois de publication</div>}
                </div>

                {/* Description */}
                <div>
                  <div style={{fontSize:'0.6875rem',fontWeight:700,color:'#9CA3AF',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:10}}>Description</div>
                  <div style={{fontSize:'0.9375rem',color:'#374151',lineHeight:1.7,whiteSpace:'pre-wrap'}}>{a.contenu}</div>
                </div>

                {/* Photos supplémentaires (galerie si > 1) */}
                {images.length > 1 && (
                  <div>
                    <div style={{fontSize:'0.6875rem',fontWeight:700,color:'#9CA3AF',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:10}}>Photos ({images.length})</div>
                    <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                      {images.map((url,i)=>(
                        <div key={i} style={{width:120,height:90,borderRadius:10,overflow:'hidden',border:'1px solid #E5E7EB',flexShrink:0,cursor:'pointer'}}
                          onClick={()=>window.open(`${API_BASE}${url}`,'_blank')}>
                          <img src={`${API_BASE}${url}`} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>

              {/* ── Footer ── */}
              <div style={{padding:'14px 26px',borderTop:'1px solid #F3F4F6',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0,backgroundColor:'#FAFAFA',borderRadius:'0 0 16px 16px'}}>
                <span style={{fontSize:'0.75rem',color:'#9CA3AF'}}>Annonce #{a.id} · soumise le {format(new Date(a.created_at),'d MMM yyyy',{locale:fr})}</span>
                <div style={{display:'flex',gap:8}}>
                  <button onClick={()=>{handleValidateAnnonce(a.id,false);setSelectedAnnonce(null);}} style={{...S.btnDanger,padding:'9px 18px',borderRadius:8,fontSize:'0.875rem'}}><XCircle style={{width:14,height:14}}/> Refuser</button>
                  <button onClick={()=>{handleValidateAnnonce(a.id,true);setSelectedAnnonce(null);}} style={{...S.btnSuccess,padding:'9px 18px',borderRadius:8,fontSize:'0.875rem',fontWeight:700}}><CheckCircle2 style={{width:14,height:14}}/> Approuver</button>
                </div>
              </div>

            </div>
          </div>
        );
      })()}

      {/* ══════════════════════════════════════════
          TAB PARAMÈTRES
      ══════════════════════════════════════════ */}
      {activeTab === 'settings' && (
        !isAdminRole ? (
          <div style={{...S.card,padding:'32px',textAlign:'center',color:'#9CA3AF'}}>
            <Lock style={{width:28,height:28,margin:'0 auto 10px',display:'block',color:'#D1D5DB'}}/>
            <div style={{fontWeight:600,fontSize:'0.9375rem',color:'#374151',marginBottom:4}}>Accès réservé aux administrateurs</div>
            <div style={{fontSize:'0.8125rem'}}>Contactez un admin pour modifier les paramètres.</div>
          </div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:20}}>

            {/* ── Section Modules ── */}
            <div style={{...S.card,overflow:'hidden'}}>
              <div style={{padding:'12px 18px',borderBottom:'1px solid #F3F4F6',display:'flex',alignItems:'center',gap:8,backgroundColor:'#FAFAFA'}}>
                <Users style={{width:15,height:15,color:'#7C3AED'}}/>
                <span style={{fontWeight:700,fontSize:'0.875rem',color:'#111827'}}>Modules</span>
                <span style={{fontSize:'0.75rem',color:'#9CA3AF',marginLeft:4}}>Activer/désactiver les modules de l'application</span>
              </div>
              <div style={{padding:'14px 18px',display:'flex',flexDirection:'column',gap:0}}>
                {[
                  { key:'module_stamm_enabled', label:'Module Stamm', desc:'Affiche le Stamm dans la navigation et permet les inscriptions aux soirées du club' },
                ].map((item, i, arr) => {
                  const isOn = settingsForm[item.key] !== 'false';
                  return (
                    <div key={item.key} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 0',borderBottom: i < arr.length-1 ? '1px solid #F9FAFB' : 'none',gap:16}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:600,fontSize:'0.875rem',color:'#111827'}}>{item.label}</div>
                        <div style={{fontSize:'0.75rem',color:'#9CA3AF',marginTop:1}}>{item.desc}</div>
                      </div>
                      <button type="button" onClick={()=>setSettingsForm(f=>({...f,[item.key]: isOn ? 'false' : 'true'}))}
                        style={{background:'none',border:'none',cursor:'pointer',padding:0,display:'flex',alignItems:'center',flexShrink:0}}>
                        {isOn
                          ? <ToggleRight style={{width:36,height:36,color:'#7C3AED'}}/>
                          : <ToggleLeft style={{width:36,height:36,color:'#D1D5DB'}}/>}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Section Workflows validation ── */}
            <div style={{...S.card,overflow:'hidden'}}>
              <div style={{padding:'12px 18px',borderBottom:'1px solid #F3F4F6',display:'flex',alignItems:'center',gap:8,backgroundColor:'#FAFAFA'}}>
                <CheckCircle2 style={{width:15,height:15,color:'#059669'}}/>
                <span style={{fontWeight:700,fontSize:'0.875rem',color:'#111827'}}>Workflows de validation</span>
                <span style={{fontSize:'0.75rem',color:'#9CA3AF',marginLeft:4}}>Activer/désactiver la validation comité avant publication</span>
              </div>
              <div style={{padding:'14px 18px',display:'flex',flexDirection:'column',gap:0}}>
                {[
                  { key:'validation_annonces', label:'Annonces', desc:'Les annonces soumises nécessitent une approbation du comité avant publication' },
                  { key:'validation_qso', label:'QSO — Capitaine', desc:'Les propositions de capitaine QSO nécessitent une validation' },
                  { key:'validation_evenements', label:'Agenda — Événements', desc:'Les événements proposés nécessitent une approbation avant publication' },
                  { key:'validation_stamm', label:'Stamm — Responsable', desc:'Les inscriptions stamm nécessitent une validation' },
                  { key:'validation_inscription', label:'Inscription membres', desc:'Les nouvelles inscriptions doivent être approuvées par un administrateur (en plus de la vérification email)' },
                ].map((item, i, arr) => {
                  const isOn = settingsForm[item.key] === 'true' || settingsForm[item.key] === true;
                  return (
                    <div key={item.key} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 0',borderBottom: i < arr.length-1 ? '1px solid #F9FAFB' : 'none',gap:16}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:600,fontSize:'0.875rem',color:'#111827'}}>{item.label}</div>
                        <div style={{fontSize:'0.75rem',color:'#9CA3AF',marginTop:1}}>{item.desc}</div>
                      </div>
                      <button type="button" onClick={()=>setSettingsForm(f=>({...f,[item.key]: isOn ? 'false' : 'true'}))}
                        style={{background:'none',border:'none',cursor:'pointer',padding:0,display:'flex',alignItems:'center',flexShrink:0}}>
                        {isOn
                          ? <ToggleRight style={{width:36,height:36,color:'#059669'}}/>
                          : <ToggleLeft style={{width:36,height:36,color:'#D1D5DB'}}/>}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Section Tableau d'affichage ── */}
            <div style={{...S.card,overflow:'hidden'}}>
              <div style={{padding:'12px 18px',borderBottom:'1px solid #F3F4F6',display:'flex',alignItems:'center',gap:8,backgroundColor:'#FAFAFA'}}>
                <Tv2 style={{width:15,height:15,color:'#0EA5E9',flexShrink:0}}/>
                <div>
                  <div style={{fontWeight:700,fontSize:'0.875rem',color:'#111827'}}>Tableau d'affichage</div>
                  <div style={{fontSize:'0.75rem',color:'#9CA3AF',marginTop:1}}>Page plein écran pour moniteur au Stamm</div>
                </div>
              </div>
              <div style={{padding:'14px 18px',display:'flex',flexDirection:'column',gap:0}}>
                {/* Toggle activation */}
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 0',borderBottom:'1px solid #F9FAFB',gap:16}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:600,fontSize:'0.875rem',color:'#111827'}}>Activer le tableau d'affichage</div>
                    <div style={{fontSize:'0.75rem',color:'#9CA3AF',marginTop:1}}>
                      La page <span style={{fontFamily:'monospace',background:'#F3F4F6',padding:'1px 5px',borderRadius:4}}>/display</span> affiche QSO, agenda, annonces et Stamm en temps réel
                    </div>
                  </div>
                  <button type="button" onClick={()=>setSettingsForm(f=>({...f,display_enabled: f.display_enabled==='true'?'false':'true'}))}
                    style={{background:'none',border:'none',cursor:'pointer',padding:0,display:'flex',alignItems:'center',flexShrink:0}}>
                    {settingsForm.display_enabled==='true'
                      ? <ToggleRight style={{width:36,height:36,color:'#0EA5E9'}}/>
                      : <ToggleLeft style={{width:36,height:36,color:'#D1D5DB'}}/>}
                  </button>
                </div>
                {/* Intervalle de rafraîchissement */}
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 0',borderBottom:'1px solid #F9FAFB',gap:16}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:600,fontSize:'0.875rem',color:'#111827'}}>Intervalle de rafraîchissement</div>
                    <div style={{fontSize:'0.75rem',color:'#9CA3AF',marginTop:1}}>En secondes (recommandé : 60)</div>
                  </div>
                  <input
                    type="number" min={10} max={600} style={{...S.inp,width:90,textAlign:'center'}}
                    value={settingsForm.display_refresh_seconds||'60'}
                    onChange={e=>setSettingsForm(f=>({...f,display_refresh_seconds:e.target.value}))}
                  />
                </div>
                {/* IPs autorisées */}
                <div style={{padding:'12px 0',borderBottom:'1px solid #F9FAFB'}}>
                  <div style={{fontWeight:600,fontSize:'0.875rem',color:'#111827',marginBottom:4}}>IPs / plages autorisées</div>
                  <div style={{fontSize:'0.75rem',color:'#9CA3AF',marginBottom:8}}>
                    Séparez par des virgules. Accepte IPs exactes, CIDR et loopback.<br/>
                    <span style={{fontFamily:'monospace',fontSize:'0.72rem',color:'#6B7280'}}>Ex : 127.0.0.1, 192.168.1.0/24, 10.0.0.5</span><br/>
                    <span style={{fontFamily:'monospace',fontSize:'0.72rem',color:'#6B7280'}}>Laisser vide = aucune restriction.</span>
                  </div>
                  <input
                    type="text"
                    style={{...S.inp, width:'100%', fontFamily:'monospace', fontSize:'0.8rem'}}
                    placeholder="127.0.0.1, ::1, 192.168.0.0/16, 10.0.0.0/8"
                    value={settingsForm.display_allowed_ips||''}
                    onChange={e=>setSettingsForm(f=>({...f,display_allowed_ips:e.target.value}))}
                  />
                </div>
                {/* Lien direct */}
                {settingsForm.display_enabled==='true' && (
                  <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:'#F0F9FF',border:'1px solid #BAE6FD',borderRadius:8,marginTop:6}}>
                    <div style={{fontSize:'0.8125rem',color:'#0369A1',flex:1}}>
                      Lien tableau :{' '}
                      <a href="/display" target="_blank" rel="noreferrer"
                        style={{fontFamily:'monospace',fontWeight:700,color:'#0EA5E9',textDecoration:'underline'}}>
                        {window.location.origin}/display
                      </a>
                    </div>
                    <button type="button"
                      onClick={()=>navigator.clipboard.writeText(window.location.origin+'/display').then(()=>showToast('Lien copié !'))}
                      style={{fontSize:'0.75rem',fontWeight:600,padding:'5px 12px',background:'#0EA5E9',color:'white',border:'none',borderRadius:6,cursor:'pointer',flexShrink:0}}>
                      Copier
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* ── Section Planning QSO de section ── */}
            <div style={{...S.card,overflow:'hidden'}}>
              <div style={{padding:'12px 18px',borderBottom:'1px solid #F3F4F6',display:'flex',alignItems:'center',gap:8,backgroundColor:'#FAFAFA'}}>
                <Radio style={{width:15,height:15,color:'#7C3AED',flexShrink:0}}/>
                <div>
                  <div style={{fontWeight:700,fontSize:'0.875rem',color:'#111827'}}>Planning QSO de section</div>
                  <div style={{fontSize:'0.75rem',color:'#9CA3AF',marginTop:1}}>Jour, heure et date d'effet du QSO hebdomadaire</div>
                </div>
              </div>
              <div style={{padding:'16px 18px',display:'flex',flexDirection:'column',gap:16}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 140px',gap:12}}>
                  <div>
                    <label style={S.label}>Jour de la semaine</label>
                    <div style={{position:'relative'}}>
                      <select
                        value={settingsForm.qso_jour ?? '4'}
                        onChange={e=>setSettingsForm(f=>({...f,qso_jour:e.target.value}))}
                        style={{...S.inp,appearance:'none',paddingRight:32,cursor:'pointer'}}
                      >
                        <option value="0">Lundi</option>
                        <option value="1">Mardi</option>
                        <option value="2">Mercredi</option>
                        <option value="3">Jeudi</option>
                        <option value="4">Vendredi</option>
                        <option value="5">Samedi</option>
                        <option value="6">Dimanche</option>
                      </select>
                      <ChevronDown style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',width:14,height:14,pointerEvents:'none',color:'#6B7280'}}/>
                    </div>
                  </div>
                  <div>
                    <label style={S.label}>Heure de début</label>
                    <input
                      type="time" style={S.inp}
                      value={settingsForm.qso_heure || '20:00'}
                      onChange={e=>setSettingsForm(f=>({...f,qso_heure:e.target.value}))}
                    />
                  </div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 120px',gap:12}}>
                  <div>
                    <label style={S.label}>
                      Date d'effet
                      <span style={{fontWeight:400,color:'#9CA3AF',marginLeft:6}}>— à partir de quand ce planning s'applique</span>
                    </label>
                    <input
                      type="date" style={S.inp}
                      value={settingsForm.qso_effectif_depuis || ''}
                      onChange={e=>setSettingsForm(f=>({...f,qso_effectif_depuis:e.target.value}))}
                    />
                  </div>
                  <div>
                    <label style={S.label}>Semaines à l'avance</label>
                    <input
                      type="number" min={1} max={52} style={S.inp}
                      value={settingsForm.qso_semaines_avance || '8'}
                      onChange={e=>setSettingsForm(f=>({...f,qso_semaines_avance:e.target.value}))}
                    />
                  </div>
                </div>

                {/* Aperçu */}
                {settingsForm.qso_effectif_depuis && (
                  <div style={{backgroundColor:'#F5F3FF',border:'1px solid #DDD6FE',borderRadius:8,padding:'10px 14px',fontSize:'0.8125rem',color:'#5B21B6'}}>
                    <strong>Aperçu :</strong> Sessions chaque{' '}
                    <strong>{['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'][parseInt(settingsForm.qso_jour??'4')]}</strong>
                    {' '}à <strong>{settingsForm.qso_heure || '20:00'}</strong>,
                    {' '}à partir du <strong>{new Date(settingsForm.qso_effectif_depuis+'T00:00:00').toLocaleDateString('fr-CH',{day:'numeric',month:'long',year:'numeric'})}</strong>
                    {' '}sur <strong>{settingsForm.qso_semaines_avance || 8} semaines</strong>.
                  </div>
                )}

                {/* Bouton générer */}
                <div style={{borderTop:'1px solid #F3F4F6',paddingTop:14,display:'flex',flexDirection:'column',gap:8}}>
                  <button
                    type="button"
                    disabled={generating || !settingsForm.qso_effectif_depuis}
                    onClick={async()=>{
                      setGenerating(true); setGenerateResult(null);
                      try {
                        const r = await adminAPI.generateQSOSessions();
                        setGenerateResult({ok:true, ...r.data});
                        loadAll();
                      } catch(e) {
                        setGenerateResult({ok:false, message: e.response?.data?.error||'Erreur'});
                      } finally { setGenerating(false); }
                    }}
                    style={{
                      width:'100%',display:'flex',alignItems:'center',justifyContent:'center',gap:8,
                      backgroundColor: generating||!settingsForm.qso_effectif_depuis ? '#F3F4F6' : '#7C3AED',
                      color: generating||!settingsForm.qso_effectif_depuis ? '#9CA3AF' : 'white',
                      border:'none',borderRadius:8,padding:'11px 18px',fontWeight:700,fontSize:'0.875rem',
                      cursor: generating||!settingsForm.qso_effectif_depuis ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {generating
                      ? <><RefreshCw style={{width:15,height:15,animation:'spin 1s linear infinite'}}/> Génération en cours...</>
                      : <><Zap style={{width:15,height:15}}/> Générer les sessions QSO</>
                    }
                  </button>
                  <p style={{fontSize:'0.75rem',color:'#9CA3AF',margin:0,textAlign:'center'}}>
                    Sauvegardez d'abord les paramètres, puis cliquez pour créer les sessions manquantes.
                  </p>
                </div>

                {generateResult && (
                  <div style={{
                    display:'flex',alignItems:'flex-start',gap:8,padding:'10px 14px',borderRadius:8,fontSize:'0.8125rem',
                    backgroundColor: generateResult.ok ? '#D1FAE5' : '#FEF2F2',
                    border: `1px solid ${generateResult.ok ? '#6EE7B7' : '#FECACA'}`,
                    color: generateResult.ok ? '#065F46' : '#DC2626',
                  }}>
                    {generateResult.ok
                      ? <CheckCircle2 style={{width:15,height:15,flexShrink:0,marginTop:1}}/>
                      : <AlertTriangle style={{width:15,height:15,flexShrink:0,marginTop:1}}/>
                    }
                    <span>{generateResult.message}{generateResult.ok && ` (${generateResult.created} créée(s), ${generateResult.skipped} existante(s))`}</span>
                  </div>
                )}
              </div>
            </div>

            {/* ── Section Mailing général ── */}
            <div style={{...S.card,overflow:'hidden'}}>
              <div style={{padding:'12px 18px',borderBottom:'1px solid #F3F4F6',display:'flex',alignItems:'center',gap:8,backgroundColor:'#FAFAFA'}}>
                <Mail style={{width:15,height:15,color:'#2563EB'}}/>
                <span style={{fontWeight:700,fontSize:'0.875rem',color:'#111827'}}>Mailing — Adresses</span>
              </div>
              <div style={{padding:'16px 18px',display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:14}}>
                {[
                  { key:'mail_from', label:'Adresse expéditeur (From)', placeholder:'noreply@hb9g.ch', type:'email' },
                  { key:'mail_from_name', label:'Nom expéditeur', placeholder:'HB9G Club', type:'text' },
                  { key:'mail_comite', label:'Email du comité (notifications)', placeholder:'comite@hb9g.ch', type:'email' },
                  { key:'mail_admin', label:'Email administrateur', placeholder:'admin@hb9g.ch', type:'email' },
                ].map(item => (
                  <div key={item.key}>
                    <label style={S.label}>{item.label}</label>
                    <input type={item.type} style={S.inp} placeholder={item.placeholder}
                      value={settingsForm[item.key] || ''}
                      onChange={e=>setSettingsForm(f=>({...f,[item.key]:e.target.value}))}/>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Section SMTP ── */}
            <div style={{...S.card,overflow:'hidden'}}>
              <div style={{padding:'12px 18px',borderBottom:'1px solid #F3F4F6',display:'flex',alignItems:'center',gap:8,backgroundColor:'#FAFAFA'}}>
                <Server style={{width:15,height:15,color:'#7C3AED'}}/>
                <span style={{fontWeight:700,fontSize:'0.875rem',color:'#111827'}}>SMTP — Serveur d'envoi</span>
              </div>
              <div style={{padding:'16px 18px',display:'flex',flexDirection:'column',gap:14}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 120px 120px',gap:12}}>
                  <div>
                    <label style={S.label}>Hôte SMTP</label>
                    <input style={S.inp} placeholder="smtp.mailgun.org" value={settingsForm.smtp_host || ''}
                      onChange={e=>setSettingsForm(f=>({...f,smtp_host:e.target.value}))}/>
                  </div>
                  <div>
                    <label style={S.label}>Port</label>
                    <input style={S.inp} type="number" placeholder="587" value={settingsForm.smtp_port || ''}
                      onChange={e=>setSettingsForm(f=>({...f,smtp_port:e.target.value}))}/>
                  </div>
                  <div>
                    <label style={S.label}>TLS / SSL</label>
                    <div style={{display:'flex',alignItems:'center',gap:8,height:38}}>
                      <button type="button" onClick={()=>setSettingsForm(f=>({...f,smtp_secure: f.smtp_secure==='true'?'false':'true'}))}
                        style={{background:'none',border:'none',cursor:'pointer',padding:0,display:'flex'}}>
                        {settingsForm.smtp_secure === 'true'
                          ? <ToggleRight style={{width:32,height:32,color:'#059669'}}/>
                          : <ToggleLeft style={{width:32,height:32,color:'#D1D5DB'}}/>}
                      </button>
                      <span style={{fontSize:'0.75rem',color:'#6B7280'}}>{settingsForm.smtp_secure === 'true' ? 'Activé' : 'Désactivé'}</span>
                    </div>
                  </div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                  <div>
                    <label style={S.label}>Utilisateur SMTP</label>
                    <input style={S.inp} placeholder="user@domain.com" value={settingsForm.smtp_user || ''}
                      onChange={e=>setSettingsForm(f=>({...f,smtp_user:e.target.value}))}/>
                  </div>
                  <div>
                    <label style={S.label}>Mot de passe SMTP</label>
                    <input style={S.inp} type="password" placeholder="••••••••" value={settingsForm.smtp_pass || ''}
                      onChange={e=>setSettingsForm(f=>({...f,smtp_pass:e.target.value}))}/>
                  </div>
                </div>
                <div style={{backgroundColor:'#F8FAFF',border:'1px solid #E0E7FF',borderRadius:7,padding:'8px 12px',fontSize:'0.75rem',color:'#6366F1'}}>
                  ℹ️ Le serveur redémarre automatiquement pour appliquer les nouveaux paramètres SMTP. Les changements prendront effet au prochain email envoyé.
                </div>
                {/* Test d'envoi */}
                <div style={{borderTop:'1px solid #F3F4F6',paddingTop:14}}>
                  <div style={{fontSize:'0.75rem',fontWeight:700,color:'#374151',marginBottom:8}}>Tester la configuration</div>
                  <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                    <input
                      type="email"
                      placeholder="Adresse destinataire du test…"
                      value={testEmailTo}
                      onChange={e=>{setTestEmailTo(e.target.value);setTestEmailResult(null);}}
                      style={{...S.inp,flex:1,minWidth:200}}
                    />
                    <button
                      type="button"
                      disabled={testEmailSending || !testEmailTo}
                      onClick={async()=>{
                        setTestEmailSending(true);
                        setTestEmailResult(null);
                        try {
                          const r = await settingsAPI.testEmail(testEmailTo);
                          setTestEmailResult({ ok:true, msg: r.data.message });
                        } catch(e) {
                          setTestEmailResult({ ok:false, msg: e.response?.data?.error || 'Erreur inconnue' });
                        } finally { setTestEmailSending(false); }
                      }}
                      style={{...S.btnPrimary,backgroundColor:'#7C3AED',opacity:(testEmailSending||!testEmailTo)?0.6:1,whiteSpace:'nowrap'}}>
                      <Mail style={{width:13,height:13}}/> {testEmailSending ? 'Envoi…' : 'Envoyer un test'}
                    </button>
                  </div>
                  {testEmailResult && (
                    <div style={{marginTop:8,padding:'7px 12px',borderRadius:6,fontSize:'0.75rem',fontWeight:600,
                      backgroundColor: testEmailResult.ok ? '#F0FDF4' : '#FEF2F2',
                      border: `1px solid ${testEmailResult.ok ? '#BBF7D0' : '#FECACA'}`,
                      color: testEmailResult.ok ? '#15803D' : '#DC2626'}}>
                      {testEmailResult.ok ? '✅' : '❌'} {testEmailResult.msg}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Section Application ── */}
            <div style={{...S.card,overflow:'hidden'}}>
              <div style={{padding:'12px 18px',borderBottom:'1px solid #F3F4F6',display:'flex',alignItems:'center',gap:8,backgroundColor:'#FAFAFA'}}>
                <Globe style={{width:15,height:15,color:'#0891B2'}}/>
                <span style={{fontWeight:700,fontSize:'0.875rem',color:'#111827'}}>Application</span>
              </div>
              <div style={{padding:'16px 18px',display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:14}}>
                {[
                  { key:'app_url', label:"URL de l'application", placeholder:'https://portail.hb9g.ch' },
                  { key:'app_name', label:"Nom de l'application", placeholder:'HB9G Portail' },
                  { key:'club_name', label:'Nom du club', placeholder:'HB9G Amateur Radio Club' },
                  { key:'club_location', label:'Localisation du club', placeholder:'Genève, Suisse' },
                ].map(item => (
                  <div key={item.key}>
                    <label style={S.label}>{item.label}</label>
                    <input style={S.inp} placeholder={item.placeholder} value={settingsForm[item.key] || ''}
                      onChange={e=>setSettingsForm(f=>({...f,[item.key]:e.target.value}))}/>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Section Signal ── */}
            <div style={{...S.card,overflow:'hidden'}}>
              <div style={{padding:'12px 18px',borderBottom:'1px solid #F3F4F6',display:'flex',alignItems:'center',justifyContent:'space-between',backgroundColor:'#FAFAFA'}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <MessageSquare style={{width:15,height:15,color:'#2563EB'}}/>
                  <span style={{fontWeight:700,fontSize:'0.875rem',color:'#111827'}}>Signal — Notifications messagerie</span>
                </div>
                <button type="button" onClick={()=>setSettingsForm(f=>({...f,signal_enabled: f.signal_enabled==='true'?'false':'true'}))}
                  style={{background:'none',border:'none',cursor:'pointer',padding:0,display:'flex',alignItems:'center',gap:6}}>
                  {settingsForm.signal_enabled === 'true'
                    ? <><ToggleRight style={{width:30,height:30,color:'#059669'}}/><span style={{fontSize:'0.75rem',color:'#059669',fontWeight:600}}>Activé</span></>
                    : <><ToggleLeft style={{width:30,height:30,color:'#D1D5DB'}}/><span style={{fontSize:'0.75rem',color:'#9CA3AF'}}>Désactivé</span></>}
                </button>
              </div>
              <div style={{padding:'16px 18px',display:'flex',flexDirection:'column',gap:14,opacity: settingsForm.signal_enabled==='true' ? 1 : 0.45, pointerEvents: settingsForm.signal_enabled==='true' ? 'auto' : 'none'}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                  <div>
                    <label style={S.label}>Numéro expéditeur Signal</label>
                    <input style={{...S.inp,fontFamily:'monospace'}} placeholder="+41791234567" value={settingsForm.signal_sender_number || ''}
                      onChange={e=>setSettingsForm(f=>({...f,signal_sender_number:e.target.value}))}/>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:3}}>
                      <div style={{fontSize:'0.6875rem',color:'#9CA3AF'}}>Numéro enregistré dans signal-cli</div>
                      {settingsForm.signal_sender_number && (
                        <button type="button" onClick={async()=>{
                          if (!window.confirm(`Désenregistrer ${settingsForm.signal_sender_number} de signal-cli ?\nCela permettra d'en configurer un nouveau via le wizard.`)) return;
                          try {
                            await settingsAPI.signalUnregister(settingsForm.signal_sender_number);
                            setSettingsForm(f=>({...f, signal_sender_number:'', signal_group_name:''}));
                            setSettings(s=>({...s, signal_sender_number:'', signal_group_name:''}));
                            setSignalWizard(true); setWizardMode(null); setWizardQR(null); setWizardStep(1); setWizardMsg(null); setWizardGroups([]);
                            showToast('Numéro désenregistré. Relancez la configuration.');
                          } catch(e) {
                            showToast(e.response?.data?.error || 'Erreur lors du désenregistrement', 'error');
                          }
                        }} style={{background:'none',border:'none',cursor:'pointer',fontSize:'0.6875rem',color:'#DC2626',fontWeight:600,padding:0}}>
                          ✕ Changer de numéro
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <label style={S.label}>Nom du groupe Signal destinataire</label>
                    <input style={S.inp} placeholder="HB9G Annonces" value={settingsForm.signal_group_name || ''}
                      onChange={e=>setSettingsForm(f=>({...f,signal_group_name:e.target.value}))}/>
                    <div style={{fontSize:'0.6875rem',color:'#9CA3AF',marginTop:3}}>Nom exact du groupe dans Signal</div>
                  </div>
                </div>
                <div>
                  <label style={S.label}>URL de l'API signal-cli REST</label>
                  <input style={S.inp} placeholder="http://localhost:8080" value={settingsForm.signal_api_url || ''}
                    onChange={e=>setSettingsForm(f=>({...f,signal_api_url:e.target.value}))}/>
                </div>

                {/* ── Lien d'invitation du groupe ── */}
                {settingsForm.signal_sender_number && settingsForm.signal_group_name && (
                  <div style={{border:'1px solid #E5E7EB',borderRadius:8,padding:'12px 14px',backgroundColor:'#FAFAFA',display:'flex',flexDirection:'column',gap:8}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
                      <span style={{fontSize:'0.8125rem',fontWeight:600,color:'#374151'}}>🔗 Lien d'invitation du groupe</span>
                      <button type="button"
                        disabled={signalGroupLink?.loading}
                        onClick={async () => {
                          setSignalGroupLink({ loading:true, link:null, groupId:null, error:null });
                          try {
                            const r = await settingsAPI.signalListGroups(settingsForm.signal_sender_number);
                            const grp = r.data.groups?.find(g => g.name?.trim().toLowerCase() === settingsForm.signal_group_name?.trim().toLowerCase());
                            if (!grp) throw new Error(`Groupe "${settingsForm.signal_group_name}" introuvable`);
                            setSignalGroupLink({ loading:false, link: grp.invite_link, groupId: grp.internal_id || grp.id, error:null });
                          } catch(e) {
                            setSignalGroupLink({ loading:false, link:null, groupId:null, error: e.response?.data?.error || e.message });
                          }
                        }}
                        style={{...S.btnPrimary,backgroundColor:'#2563EB'}}>
                        <MessageSquare style={{width:12,height:12}}/> {signalGroupLink?.loading ? 'Chargement…' : 'Charger le lien'}
                      </button>
                    </div>
                    {signalGroupLink?.error && (
                      <div style={{fontSize:'0.75rem',color:'#DC2626',backgroundColor:'#FEF2F2',border:'1px solid #FECACA',borderRadius:6,padding:'6px 10px'}}>❌ {signalGroupLink.error}</div>
                    )}
                    {signalGroupLink?.link && (
                      <div style={{display:'flex',flexDirection:'column',gap:6}}>
                        <div style={{display:'flex',alignItems:'center',gap:6,backgroundColor:'white',border:'1px solid #D1D5DB',borderRadius:6,padding:'6px 10px'}}>
                          <span style={{flex:1,fontSize:'0.75rem',fontFamily:'monospace',color:'#374151',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{signalGroupLink.link}</span>
                          <button type="button" onClick={()=>{ navigator.clipboard.writeText(signalGroupLink.link); showToast('Lien copié !'); }}
                            style={{background:'none',border:'none',cursor:'pointer',fontSize:'0.6875rem',color:'#2563EB',fontWeight:600,padding:'0 4px',flexShrink:0}}>
                            Copier
                          </button>
                        </div>
                        <button type="button"
                          onClick={async () => {
                            if (!window.confirm('Réinitialiser le lien d\'invitation ? L\'ancien lien ne fonctionnera plus.')) return;
                            setSignalGroupLink(s => ({...s, loading:true}));
                            try {
                              const r = await settingsAPI.signalResetGroupLink(settingsForm.signal_sender_number, signalGroupLink.groupId);
                              setSignalGroupLink(s => ({...s, loading:false, link: r.data.invite_link }));
                              showToast('Lien réinitialisé.');
                            } catch(e) {
                              setSignalGroupLink(s => ({...s, loading:false, error: e.response?.data?.error || e.message }));
                            }
                          }}
                          style={{...S.btnDanger,alignSelf:'flex-start'}}>
                          <XCircle style={{width:12,height:12}}/> Révoquer et recréer le lien
                        </button>
                      </div>
                    )}
                    {!signalGroupLink && (
                      <div style={{fontSize:'0.6875rem',color:'#9CA3AF'}}>Cliquez sur "Charger le lien" pour afficher le lien d'invitation actuel du groupe Signal.</div>
                    )}
                  </div>
                )}

                {/* ── Wizard configuration Signal ── */}
                <div style={{border:'1px solid #BFDBFE',borderRadius:8,overflow:'hidden'}}>
                  <button
                    type="button"
                    onClick={()=>{ setSignalWizard(v=>{ if(!v && !settingsForm.signal_api_url) setSettingsForm(f=>({...f, signal_api_url:'http://localhost:8080'})); return !v; }); setWizardStep(1); setWizardMsg(null); setWizardGroups([]); setWizardMode(null); setWizardQR(null); }}
                    style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 14px',background:'#EFF6FF',border:'none',cursor:'pointer',fontSize:'0.8125rem',fontWeight:700,color:'#1D4ED8'}}>
                    <span>🛠 Configurer signal-cli pas à pas</span>
                    <span style={{fontSize:'0.75rem',fontWeight:400}}>{signalWizard ? '▲ Fermer' : '▼ Ouvrir'}</span>
                  </button>

                  {signalWizard && (
                    <div style={{padding:'14px 16px',backgroundColor:'#F8FBFF',display:'flex',flexDirection:'column',gap:14}}>

                      <div style={{backgroundColor:'#FEF3C7',border:'1px solid #F59E0B',borderRadius:6,padding:'8px 12px',fontSize:'0.75rem',color:'#92400E',lineHeight:1.5}}>
                        <strong>Numéro dédié requis</strong> — signal-cli a besoin d'un numéro de téléphone dédié (2ème SIM ou numéro virtuel). Ce numéro ne pourra plus être utilisé avec l'app Signal mobile.
                      </div>

                      {/* ── Étapes indicateur ── */}
                      {(<>
                      <div style={{display:'flex',alignItems:'center',gap:0}}>
                        {[{n:1,label:'Envoyer SMS'},{n:2,label:'Vérifier code'},{n:3,label:'Rejoindre groupe'}].map((s,i)=>(
                          <React.Fragment key={s.n}>
                            <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
                              <div style={{width:28,height:28,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.75rem',fontWeight:800,
                                backgroundColor: wizardStep > s.n ? '#059669' : wizardStep === s.n ? '#2563EB' : '#E5E7EB',
                                color: wizardStep >= s.n ? 'white' : '#9CA3AF'}}>
                                {wizardStep > s.n ? '✓' : s.n}
                              </div>
                              <span style={{fontSize:'0.625rem',fontWeight:600,color:wizardStep===s.n?'#2563EB':'#9CA3AF',whiteSpace:'nowrap'}}>{s.label}</span>
                            </div>
                            {i < 2 && <div style={{flex:1,height:2,backgroundColor:wizardStep > s.n+0 ? '#059669' : '#E5E7EB',margin:'0 4px',marginBottom:16}}/>}
                          </React.Fragment>
                        ))}
                      </div>

                      {/* Étape 1 — Register */}
                      {wizardStep === 1 && (
                        <div style={{display:'flex',flexDirection:'column',gap:10}}>
                          <div style={{fontSize:'0.8125rem',color:'#374151',fontWeight:600}}>Étape 1 — Enregistrer le numéro Signal</div>
                          <div style={{fontSize:'0.75rem',color:'#6B7280'}}>Signal enverra un SMS de vérification à ce numéro.</div>
                          <input
                            style={{...S.inp,fontFamily:'monospace'}}
                            placeholder="+41791234567"
                            value={wizardNumber}
                            onChange={e=>{ setWizardNumber(e.target.value); setWizardMsg(null); setWizardNeedCaptcha(false); setWizardCaptcha(''); }}
                          />
                          {/* Champ captcha — affiché uniquement si Signal le demande */}
                          {wizardNeedCaptcha && (
                            <div style={{display:'flex',flexDirection:'column',gap:6,backgroundColor:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:7,padding:'10px 12px'}}>
                              <div style={{fontSize:'0.75rem',fontWeight:700,color:'#92400E'}}>🔒 Signal exige un captcha pour ce numéro</div>
                              <ol style={{margin:'0 0 6px 16px',padding:0,fontSize:'0.75rem',color:'#78350F',lineHeight:1.7}}>
                                <li>Ouvre ce lien : <a href="https://signalcaptchas.org/registration/generate.html" target="_blank" rel="noreferrer" style={{color:'#2563EB',fontWeight:600}}>signalcaptchas.org</a></li>
                                <li>Résous le captcha sur la page</li>
                                <li>Fais un <strong>clic droit</strong> sur le bouton <em>"Open Signal"</em></li>
                                <li>Copie le lien → colle-le ci-dessous</li>
                              </ol>
                              <input
                                style={{...S.inp,fontSize:'0.75rem',fontFamily:'monospace'}}
                                placeholder="signalcaptcha://signal-recaptcha-v2...."
                                value={wizardCaptcha}
                                onChange={e=>{ setWizardCaptcha(e.target.value); setWizardMsg(null); }}
                              />
                            </div>
                          )}
                          <button
                            type="button"
                            disabled={wizardLoading || !wizardNumber.trim() || (wizardNeedCaptcha && !wizardCaptcha.trim())}
                            onClick={async()=>{
                              setWizardLoading(true); setWizardMsg(null);
                              try {
                                const r = await settingsAPI.signalRegister(wizardNumber.trim(), wizardNeedCaptcha ? wizardCaptcha.trim() : undefined);
                                setWizardMsg({ ok:true, text: r.data.message });
                                setWizardNeedCaptcha(false);
                                setWizardStep(2);
                              } catch(e) {
                                const data = e.response?.data;
                                const errText = data?.error || '';
                                if (data?.captchaRequired) {
                                  setWizardNeedCaptcha(true);
                                  setWizardMsg({ ok:false, text: 'Signal exige un captcha. Suivez les instructions ci-dessous.' });
                                } else if (errText.toLowerCase().includes('already registered')) {
                                  setSettingsForm(f=>({...f, signal_sender_number: wizardNumber.trim()}));
                                  setWizardMsg({ ok:true, text: `✅ Ce numéro est déjà enregistré. Collez le lien d'invitation du groupe.` });
                                  setWizardStep(3);
                                } else {
                                  setWizardMsg({ ok:false, text: errText || 'Erreur inconnue' });
                                }
                              } finally { setWizardLoading(false); }
                            }}
                            style={{...S.btnPrimary,backgroundColor:'#2563EB',alignSelf:'flex-start',opacity:wizardLoading||!wizardNumber.trim()||(wizardNeedCaptcha&&!wizardCaptcha.trim())?0.5:1}}>
                            {wizardLoading ? '…' : wizardNeedCaptcha ? 'Renvoyer avec captcha' : 'Envoyer SMS'}
                          </button>
                        </div>
                      )}

                      {/* Étape 2 — Verify */}
                      {wizardStep === 2 && (
                        <div style={{display:'flex',flexDirection:'column',gap:10}}>
                          <div style={{fontSize:'0.8125rem',color:'#374151',fontWeight:600}}>Étape 2 — Confirmer le code SMS reçu sur <span style={{fontFamily:'monospace',color:'#2563EB'}}>{wizardNumber}</span></div>
                          <div style={{display:'flex',gap:8}}>
                            <input
                              style={{...S.inp,flex:1,fontFamily:'monospace',letterSpacing:'0.15em',textAlign:'center',fontSize:'1.1rem'}}
                              placeholder="123456"
                              maxLength={8}
                              value={wizardCode}
                              onChange={e=>{ setWizardCode(e.target.value.replace(/\D/g,'')); setWizardMsg(null); }}
                            />
                            <button
                              type="button"
                              disabled={wizardLoading || wizardCode.length < 4}
                              onClick={async()=>{
                                setWizardLoading(true); setWizardMsg(null);
                                try {
                                  const r = await settingsAPI.signalVerify(wizardNumber.trim(), wizardCode.trim());
                                  setWizardMsg({ ok:true, text: `${r.data.message} Collez le lien d'invitation du groupe.` });
                                  setSettingsForm(f=>({...f, signal_sender_number: wizardNumber.trim()}));
                                  setWizardStep(3);
                                } catch(e) {
                                  setWizardMsg({ ok:false, text: e.response?.data?.error || 'Code invalide' });
                                } finally { setWizardLoading(false); }
                              }}
                              style={{...S.btnPrimary,backgroundColor:'#059669',whiteSpace:'nowrap',opacity:wizardLoading||wizardCode.length<4?0.5:1}}>
                              {wizardLoading ? '…' : 'Vérifier'}
                            </button>
                          </div>
                          <button type="button" onClick={()=>{ setWizardStep(1); setWizardMsg(null); }} style={{alignSelf:'flex-start',background:'none',border:'none',cursor:'pointer',fontSize:'0.75rem',color:'#9CA3AF'}}>← Recommencer</button>
                        </div>
                      )}

                      {/* Étape 3 — Rejoindre un groupe via lien d'invitation */}
                      {wizardStep === 3 && (
                        <div style={{display:'flex',flexDirection:'column',gap:10}}>
                          <div style={{fontSize:'0.8125rem',color:'#374151',fontWeight:600}}>Étape 3 — Rejoindre le groupe Signal</div>
                          <div style={{fontSize:'0.75rem',color:'#6B7280',lineHeight:1.6}}>
                            Collez le <strong>lien d'invitation</strong> de votre groupe Signal existant. Pour l'obtenir :
                          </div>
                          <ol style={{margin:'0 0 0 16px',padding:0,fontSize:'0.75rem',color:'#374151',lineHeight:1.9}}>
                            <li>Ouvrez le groupe dans <strong>Signal</strong> sur votre téléphone</li>
                            <li>Paramètres du groupe → <strong>Lien du groupe</strong> → Activer</li>
                            <li><strong>Copiez le lien</strong> et collez-le ci-dessous</li>
                          </ol>
                          <input
                            style={{...S.inp,fontFamily:'monospace',fontSize:'0.75rem'}}
                            placeholder="https://signal.group/#CjQKI..."
                            value={wizardNewGroupName}
                            onChange={e=>{ setWizardNewGroupName(e.target.value); setWizardMsg(null); }}
                          />
                          <button
                            type="button"
                            disabled={wizardLoading || !wizardNewGroupName.trim().startsWith('https://signal.group/')}
                            onClick={async()=>{
                              setWizardLoading(true); setWizardMsg(null);
                              try {
                                const activeNumber = wizardNumber.trim() || settingsForm.signal_sender_number || '';
                                const r = await settingsAPI.signalJoinGroup(activeNumber, wizardNewGroupName.trim());
                                const gName = r.data.groupName || '?';
                                setSettingsForm(f=>({...f, signal_group_name: gName, signal_enabled: 'true'}));
                                setWizardMsg({ ok:true, text:`✅ Groupe "${gName}" rejoint et Signal activé. Cliquez sur Sauvegarder.` });
                              } catch(e) {
                                setWizardMsg({ ok:false, text: e.response?.data?.error || 'Erreur' });
                              } finally { setWizardLoading(false); }
                            }}
                            style={{...S.btnPrimary,backgroundColor:'#059669',alignSelf:'flex-start',opacity:wizardLoading||!wizardNewGroupName.trim().startsWith('https://signal.group/')?0.5:1}}>
                            {wizardLoading ? 'Connexion au groupe…' : 'Rejoindre le groupe'}
                          </button>
                        </div>
                      )}
                      </>)}

                      {/* Message feedback */}
                      {wizardMsg && (
                        <div style={{padding:'7px 12px',borderRadius:6,fontSize:'0.75rem',fontWeight:600,
                          backgroundColor: wizardMsg.ok ? '#F0FDF4' : '#FEF2F2',
                          border:`1px solid ${wizardMsg.ok ? '#BBF7D0' : '#FECACA'}`,
                          color: wizardMsg.ok ? '#15803D' : '#DC2626'}}>
                          {wizardMsg.text}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Bouton test Signal */}
                <div style={{borderTop:'1px solid #F3F4F6',paddingTop:14}}>
                  <div style={{fontSize:'0.75rem',fontWeight:700,color:'#374151',marginBottom:8}}>Tester la configuration</div>
                  <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                    <button
                      type="button"
                      disabled={testSignalSending || settingsForm.signal_enabled !== 'true'}
                      onClick={async()=>{
                        setTestSignalSending(true);
                        setTestSignalResult(null);
                        try {
                          const r = await settingsAPI.testSignal();
                          setTestSignalResult({ ok:true, msg: r.data.message });
                        } catch(e) {
                          setTestSignalResult({ ok:false, msg: e.response?.data?.error || 'Erreur inconnue' });
                        } finally { setTestSignalSending(false); }
                      }}
                      style={{...S.btnPrimary,backgroundColor:'#2563EB',opacity:(testSignalSending||settingsForm.signal_enabled!=='true')?0.5:1,whiteSpace:'nowrap'}}>
                      <MessageSquare style={{width:13,height:13}}/> {testSignalSending ? 'Envoi…' : 'Envoyer un test Signal'}
                    </button>
                    {settingsForm.signal_enabled !== 'true' && (
                      <span style={{fontSize:'0.75rem',color:'#9CA3AF'}}>Activez Signal pour tester</span>
                    )}
                  </div>
                  {testSignalResult && (
                    <div style={{marginTop:8,padding:'7px 12px',borderRadius:6,fontSize:'0.75rem',fontWeight:600,
                      backgroundColor: testSignalResult.ok ? '#F0FDF4' : '#FEF2F2',
                      border: `1px solid ${testSignalResult.ok ? '#BBF7D0' : '#FECACA'}`,
                      color: testSignalResult.ok ? '#15803D' : '#DC2626'}}>
                      {testSignalResult.ok ? '✅' : '❌'} {testSignalResult.msg}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Bouton Sauvegarder ── */}
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',backgroundColor:'white',border:'1px solid #E5E7EB',borderRadius:10,padding:'14px 18px',boxShadow:'0 1px 3px rgba(0,0,0,0.06)'}}>
              <div style={{fontSize:'0.8125rem',color:'#9CA3AF'}}>
                {JSON.stringify(settingsForm) !== JSON.stringify(settings)
                  ? <span style={{color:'#D97706',fontWeight:600}}>⚠ Modifications non sauvegardées</span>
                  : <span style={{color:'#059669',fontWeight:500}}>✓ Paramètres à jour</span>}
              </div>
              <div style={{display:'flex',gap:8}}>
                <button onClick={()=>setSettingsForm(settings)} style={S.btnGhost} disabled={settingsSaving}>Annuler</button>
                <button onClick={async()=>{
                  setSettingsSaving(true);
                  try { await settingsAPI.update(settingsForm); setSettings({...settingsForm}); showToast('Paramètres sauvegardés.'); }
                  catch(e){ showToast(e.response?.data?.error||'Erreur','error'); }
                  finally{ setSettingsSaving(false); }
                }} style={{...S.btnPrimary,backgroundColor:'#4F46E5',opacity:settingsSaving?0.7:1}} disabled={settingsSaving}>
                  <Save style={{width:13,height:13}}/> {settingsSaving ? 'Sauvegarde…' : 'Sauvegarder'}
                </button>
              </div>
            </div>

          </div>
        )
      )}

      {/* ── Modal de refus ── */}
      {rejectModal && (
        <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.5)',zIndex:1200,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={()=>setRejectModal(null)}>
          <div style={{backgroundColor:'white',borderRadius:14,padding:'28px',maxWidth:440,width:'100%',boxShadow:'0 24px 60px rgba(0,0,0,0.25)'}} onClick={e=>e.stopPropagation()}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16}}>
              <div style={{width:40,height:40,borderRadius:'50%',backgroundColor:'#FEF2F2',display:'flex',alignItems:'center',justifyContent:'center'}}>
                <XCircle style={{width:20,height:20,color:'#DC2626'}}/>
              </div>
              <div>
                <div style={{fontWeight:700,fontSize:'1rem',color:'#111827'}}>Refuser {rejectModal.title}</div>
                <div style={{fontSize:'0.8125rem',color:'#6B7280'}}>Le membre sera notifié par email</div>
              </div>
            </div>
            <div style={{marginBottom:16}}>
              <label style={{display:'block',fontSize:'0.8125rem',fontWeight:600,color:'#374151',marginBottom:6}}>Raison du refus <span style={{fontWeight:400,color:'#9CA3AF'}}>(optionnel)</span></label>
              <textarea
                value={rejectReason}
                onChange={e=>setRejectReason(e.target.value)}
                placeholder="Ex: Information manquante, photo de mauvaise qualité…"
                style={{width:'100%',padding:'10px 12px',border:'1px solid #D1D5DB',borderRadius:8,fontSize:'0.875rem',outline:'none',resize:'vertical',minHeight:80,boxSizing:'border-box',color:'#111827',fontFamily:'inherit'}}
                autoFocus
              />
            </div>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button onClick={()=>setRejectModal(null)} style={{...S.btnGhost,padding:'8px 16px',fontSize:'0.8125rem'}}>Annuler</button>
              <button onClick={confirmReject} style={{...S.btnDanger,padding:'8px 16px',fontSize:'0.8125rem'}}>
                <XCircle style={{width:13,height:13}}/> Confirmer le refus
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal de confirmation ── */}
      {confirm && (
        <div style={{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.45)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={()=>setConfirm(null)}>
          <div style={{backgroundColor:'white',borderRadius:12,padding:'24px 28px',maxWidth:420,width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.25)'}} onClick={e=>e.stopPropagation()}>
            <div style={{fontWeight:700,fontSize:'1rem',color:'#111827',marginBottom:10}}>Confirmer l'action</div>
            <p style={{color:'#6B7280',fontSize:'0.9rem',margin:'0 0 24px',lineHeight:1.6}}>{confirm.message}</p>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button onClick={()=>setConfirm(null)} style={S.btnGhost}>Annuler</button>
              <button onClick={()=>{ confirm.onConfirm(); setConfirm(null); }} style={S.btnDanger}>Confirmer</button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={()=>setToast(null)}/>}
    </div>
  );
};

export default ComitePage;
